import { useEffect, useState } from "react";
import { useAuth, startGoogleLogin, adminLogin } from "../lib/auth";
import { api, imgUrl } from "../lib/api";
import { Plus, Pencil, Trash2, Upload, Loader2, KeyRound, ArrowUp, ArrowDown, GripVertical } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";

const BLANK_VIDEO = { youtube_id: "", title: "", position: 0, active: true };
const BLANK_TRACK = { youtube_id: "", audio_url: "", title: "", artist: "", position: 0, active: true, media_type: "audio", media_types: ["audio"], genre: "", profession: "", artwork_url: "" };
const BLANK_MUSIC = { title: "", artist: "", src: "", position: 0, active: true };
const BLANK_PHOTO = { image_path: "", caption: "", position: 0, section: 1, active: true, artists: [] };
const BLANK_ADMIN = { username: "", password: "", name: "" };
const BLANK_STREAMER = { name: "", platform: "twitch", handle: "", position: 0, active: true };
const BLANK_CLIP = { title: "", youtube_id: "", game: "", position: 0, active: true };
const BLANK_VOD = { title: "", video_id: "", position: 0, active: true };
const BLANK_FEATURE_VIDEO = {
  title: "", description: "", source_type: "youtube",
  youtube_id: "", file_path: "", dropbox_url: "", poster_path: "",
  position: 0, active: true,
};
const BLANK_SQUAD = {
  name: "", gamertag: "", platform: "pc", favorite_games: [],
  twitch_handle: "", youtube_handle: "", discord_handle: "", avatar_path: "",
  position: 0, active: true,
};
const BLANK_ARTIST = {
  name: "", role: "", photo_path: "", bio: "",
  instagram_url: "", twitter_url: "", youtube_url: "", spotify_url: "",
  apple_music_url: "", tiktok_url: "", soundcloud_url: "", facebook_url: "", website_url: "",
  position: 0, active: true,
};

// Icon keys the About page understands for service cards
const ICON_MAP_ADMIN = {
  music: true, pen: true, palette: true, camera: true,
  globe: true, shirt: true, mic: true, sparkles: true,
};

// Field renderers for the About admin — hoisted so React doesn't remount on every keystroke.
function AboutField({ label, value, onChange, textarea = false, rows = 3, placeholder = "", testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest text-white/50">{label}</label>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="w-full mt-1 bg-black border border-white/10 focus:border-[#E63946] outline-none p-3 font-mono text-sm leading-relaxed resize-y"
          data-testid={testid}
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full mt-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}

function AboutImageField({ label, value, onChange, onUpload, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] tracking-widest text-white/50">{label}</label>
      <div className="flex items-start gap-3 mt-1">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/about/x.jpg or full URL"
          className="flex-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-sm"
          data-testid={testid}
        />
        <label className="cursor-pointer font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] transition-colors">
          UPLOAD
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) onUpload(e.target.files[0]);
            }}
          />
        </label>
      </div>
      {value && (
        <img src={imgUrl(value)} alt="" className="mt-2 max-h-32 border border-white/10 object-cover" />
      )}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading, refresh } = useAuth();
  const [tab, setTab] = useState("home");

  // collections
  const [contacts, setContacts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [music, setMusic] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [streamers, setStreamers] = useState([]);
  const [clips, setClips] = useState([]);
  const [vods, setVods] = useState([]);
  const [squad, setSquad] = useState([]);
  const [gamingConfig, setGamingConfig] = useState({ discord_invite_url: "" });
  const [artists, setArtists] = useState([]);
  const [featureVideos, setFeatureVideos] = useState([]);
  const [homeConfig, setHomeConfig] = useState(null);
  const [games, setGames] = useState([]);
  const [gameDrafts, setGameDrafts] = useState({}); // slug -> { title, html }
  const [savingGame, setSavingGame] = useState(null); // slug being saved
  const [showNewGame, setShowNewGame] = useState(false);
  const [newGameForm, setNewGameForm] = useState({ slug: "", title: "", html: "", position: 0 });
  const [creatingGame, setCreatingGame] = useState(false);
  const [trafficRange, setTrafficRange] = useState(30);
  const [trafficData, setTrafficData] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [traffic90, setTraffic90] = useState(null);
  const [aboutConfig, setAboutConfig] = useState(null);
  const [aboutDraft, setAboutDraft] = useState(null);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [aboutSection, setAboutSection] = useState("hero");
  const [playerConfig, setPlayerConfig] = useState(null);
  const [playerDraft, setPlayerDraft] = useState(null);
  const [playerSaving, setPlayerSaving] = useState(false);
  const [quickAddByType, setQuickAddByType] = useState({}); // { [mediaTypeKey]: { title, artist, youtube_id, audio_url } }
  const [quickAdding, setQuickAdding] = useState(null); // media_type key being saved

  // editing states
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoForm, setVideoForm] = useState(BLANK_VIDEO);
  const [editingTrack, setEditingTrack] = useState(null);
  const [trackForm, setTrackForm] = useState(BLANK_TRACK);
  const [libraryFilterType, setLibraryFilterType] = useState(""); // "" = all
  const [libraryFilterCreator, setLibraryFilterCreator] = useState(""); // "" = all | "any" = any creator | "<user_id>" specific
  const [librarySearch, setLibrarySearch] = useState("");
  const [assignBusy, setAssignBusy] = useState(null); // track id being toggled
  // Drag-and-drop reordering state (per-media-type block on the Player tab).
  // dragTypeKey = which media-type list is currently being dragged inside;
  // dragTrackId = id of the row picked up; dragOverId = row currently under
  // the cursor (used to render the blue drop indicator).
  const [dragTypeKey, setDragTypeKey] = useState(null);
  const [dragTrackId, setDragTrackId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverPos, setDragOverPos] = useState("before"); // "before" | "after"
  const [editingMusic, setEditingMusic] = useState(null);
  const [musicForm, setMusicForm] = useState(BLANK_MUSIC);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [photoForm, setPhotoForm] = useState(BLANK_PHOTO);
  const [editingStreamer, setEditingStreamer] = useState(null);
  const [streamerForm, setStreamerForm] = useState(BLANK_STREAMER);
  const [editingClip, setEditingClip] = useState(null);
  const [clipForm, setClipForm] = useState(BLANK_CLIP);
  const [editingVod, setEditingVod] = useState(null);
  const [vodForm, setVodForm] = useState(BLANK_VOD);
  const [editingSquadMember, setEditingSquadMember] = useState(null);
  const [squadForm, setSquadForm] = useState(BLANK_SQUAD);
  const [editingArtist, setEditingArtist] = useState(null);
  const [artistForm, setArtistForm] = useState(BLANK_ARTIST);
  const [editingFeatureVideo, setEditingFeatureVideo] = useState(null);
  const [featureVideoForm, setFeatureVideoForm] = useState(BLANK_FEATURE_VIDEO);
  const [newAdminForm, setNewAdminForm] = useState(BLANK_ADMIN);
  // Creators (content contributor role)
  const [creators, setCreators] = useState([]);
  const [newCreatorForm, setNewCreatorForm] = useState({ username: "", password: "", name: "" });
  const [expandedCreator, setExpandedCreator] = useState(null);
  const [creatorUploads, setCreatorUploads] = useState({}); // { user_id: { tracks: [], photos: [] } }
  const [creatorPwPrompt, setCreatorPwPrompt] = useState(null); // { user_id, value }

  // login form
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchAll = async () => {
    // Use independent promises so a single failure doesn't wipe everything.
    const safeGet = (url, setter) =>
      api.get(url).then((r) => setter(r.data || [])).catch((err) => {
        console.error(`Admin fetch failed: ${url}`, err);
      });
    await Promise.allSettled([
      safeGet("/admin/contacts", setContacts),
      safeGet("/videos", setVideos),
      safeGet("/gallery", setPhotos),
      safeGet("/gallery/sections", setSections),
      safeGet("/admin/users", setUsers),
      safeGet("/admin/creators", setCreators),
      safeGet("/playlist", setTracks),
      safeGet("/music", setMusic),
      safeGet("/gaming/streamers", setStreamers),
      safeGet("/gaming/clips", setClips),
      safeGet("/gaming/twitch-vods", setVods),
      safeGet("/gaming/squad", setSquad),
      safeGet("/artists", setArtists),
      safeGet("/feature-videos", setFeatureVideos),
      safeGet("/admin/games", (list) => {
        setGames(list);
        const drafts = {};
        for (const g of list) drafts[g.slug] = { title: g.title, html: g.html };
        setGameDrafts(drafts);
      }),
      api.get("/home/config").then((r) => setHomeConfig(r.data || null)).catch(() => {}),
      api.get("/about/config").then((r) => { setAboutConfig(r.data || null); setAboutDraft(r.data || null); }).catch(() => {}),
      api.get("/player/config").then((r) => { setPlayerConfig(r.data || null); setPlayerDraft(r.data || null); }).catch(() => {}),
      api.get("/gaming/config").then((r) => setGamingConfig(r.data || { discord_invite_url: "" })).catch(() => {}),
    ]);
  };

  useEffect(() => { if (user?.role === "admin") fetchAll(); }, [user]);
  // Load the 90-day rollup banner
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/analytics/summary?days=90")
      .then((r) => setTraffic90(r.data))
      .catch(() => { /* silent */ });
  }, [user]);

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error("Username and password required");
      return;
    }
    setLoginLoading(true);
    try {
      await adminLogin(loginForm.username, loginForm.password);
      await refresh();
      toast.success("Welcome back");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoginLoading(false);
    }
  };

  // ---------- uploads ----------
  const uploadFile = async (file, kind, callback) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      callback(res.data);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // ---------- videos ----------
  const startEditVideo = (v) => { setEditingVideo(v.id); setVideoForm(v); };
  const newVideo = () => { setEditingVideo("new"); setVideoForm({ ...BLANK_VIDEO, position: videos.length + 1 }); };
  const saveVideo = async () => {
    if (!videoForm.youtube_id) { toast.error("YouTube ID or URL required"); return; }
    try {
      if (editingVideo === "new") {
        await api.post("/admin/videos", videoForm);
      } else {
        await api.put(`/admin/videos/${editingVideo}`, videoForm);
      }
      toast.success("Saved");
      setEditingVideo(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteVideo = async (id) => {
    if (!window.confirm("Remove this video?")) return;
    await api.delete(`/admin/videos/${id}`);
    fetchAll();
  };

  // ---------- playlist tracks ----------
  const startEditTrack = (t) => {
    setEditingTrack(t.id);
    const media_types = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
    setTrackForm({ ...t, media_types, media_type: media_types[0] });
  };
  const newTrack = () => { setEditingTrack("new"); setTrackForm({ ...BLANK_TRACK, position: tracks.length + 1 }); };
  // Toggle a section (media_type) assignment inline from the Library row, without opening the editor.
  const toggleTrackSection = async (t, key) => {
    const cur = Array.isArray(t.media_types) && t.media_types.length ? [...t.media_types] : [t.media_type || "audio"];
    let next;
    if (cur.includes(key)) {
      next = cur.filter((k) => k !== key);
      if (next.length === 0) {
        toast.error("A track must belong to at least one section — delete instead if not needed.");
        return;
      }
    } else {
      next = [...cur, key];
    }
    setAssignBusy(t.id);
    try {
      await api.put(`/admin/playlist/${t.id}`, {
        youtube_id: t.youtube_id || "",
        audio_url: t.audio_url || "",
        title: t.title || "",
        artist: t.artist || "",
        position: t.position || 0,
        active: t.active !== false,
        media_type: next[0],
        media_types: next,
        genre: t.genre || "",
        profession: t.profession || "",
        artwork_url: t.artwork_url || "",
      });
      const r = await api.get("/playlist");
      setTracks(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setAssignBusy(null);
    }
  };
  const saveTrack = async () => {
    if (!trackForm.youtube_id && !trackForm.audio_url) { toast.error("YouTube ID/URL or audio URL is required"); return; }
    try {
      if (editingTrack === "new") {
        await api.post("/admin/playlist", trackForm);
      } else {
        await api.put(`/admin/playlist/${editingTrack}`, trackForm);
      }
      toast.success("Saved");
      setEditingTrack(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteTrack = async (id) => {
    if (!window.confirm("Remove this track?")) return;
    await api.delete(`/admin/playlist/${id}`);
    fetchAll();
  };

  // Swap a playlist track with its neighbor in the requested direction.
  const moveTrack = async (id, dir) => {
    const sorted = [...tracks].sort((a, b) => (a.position || 0) - (b.position || 0));
    const i = sorted.findIndex((t) => t.id === id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= sorted.length) return;
    const a = sorted[i];
    const b = sorted[j];
    try {
      await Promise.all([
        api.put(`/admin/playlist/${a.id}`, { ...a, position: b.position }),
        api.put(`/admin/playlist/${b.id}`, { ...b, position: a.position }),
      ]);
      fetchAll();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  // Swap a playlist track with its neighbor WITHIN a specific media type. This
  // gives the operator fine-grained control per Player pill (AUDIO / VIDEO /
  // INSTRUMENTAL / PODCAST) — the swapped neighbor is the previous/next track
  // that also belongs to `key`, not the global neighbor.
  const moveTrackInType = async (id, dir, key) => {
    const typeSorted = tracks
      .filter((t) => {
        const types = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
        return types.includes(key);
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    const i = typeSorted.findIndex((t) => t.id === id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= typeSorted.length) return;
    const a = typeSorted[i];
    const b = typeSorted[j];
    try {
      await Promise.all([
        api.put(`/admin/playlist/${a.id}`, { ...a, position: b.position }),
        api.put(`/admin/playlist/${b.id}`, { ...b, position: a.position }),
      ]);
      const r = await api.get("/playlist");
      setTracks(r.data || []);
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  // Drop `draggedId` before/after `targetId` inside the given media type list.
  // Positions are permuted: we take the ordered list of existing position
  // values and re-assign them to the new sequence, so only the tracks whose
  // slot actually changed get a PUT (batched). This preserves every track's
  // "global" position bucket for the other media types it belongs to.
  const reorderTrackInType = async (draggedId, targetId, key, dropAfter = false) => {
    if (!draggedId || !targetId || draggedId === targetId) return;
    const list = tracks
      .filter((t) => {
        const types = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
        return types.includes(key);
      })
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    const dragged = list.find((t) => t.id === draggedId);
    if (!dragged) return;
    const positions = list.map((t) => t.position || 0);
    const remaining = list.filter((t) => t.id !== draggedId);
    const targetIdx = remaining.findIndex((t) => t.id === targetId);
    if (targetIdx < 0) return;
    const insertAt = dropAfter ? targetIdx + 1 : targetIdx;
    const newOrder = [
      ...remaining.slice(0, insertAt),
      dragged,
      ...remaining.slice(insertAt),
    ];
    // Compute minimal PUT set — only tracks whose position slot changed.
    const updates = [];
    for (let i = 0; i < newOrder.length; i++) {
      const desired = positions[i];
      if ((newOrder[i].position || 0) !== desired) {
        updates.push({ ...newOrder[i], position: desired });
      }
    }
    if (!updates.length) return;
    // Optimistic UI — patch local state so the row moves before the network.
    setTracks((prev) => prev.map((t) => {
      const u = updates.find((x) => x.id === t.id);
      return u ? { ...t, position: u.position } : t;
    }));
    try {
      await Promise.all(updates.map((u) => api.put(`/admin/playlist/${u.id}`, u)));
      const r = await api.get("/playlist");
      setTracks(r.data || []);
    } catch (e) {
      toast.error("Reorder failed");
      // Roll back by re-fetching.
      try { const r = await api.get("/playlist"); setTracks(r.data || []); } catch (_) { /* noop */ }
    }
  };

  // ---------- music catalog (Dropbox audio tracks) ----------
  const startEditMusic = (t) => { setEditingMusic(t.id); setMusicForm(t); };
  const newMusic = () => { setEditingMusic("new"); setMusicForm({ ...BLANK_MUSIC, position: music.length + 1 }); };
  const saveMusic = async () => {
    if (!musicForm.title || !musicForm.src) { toast.error("Title and audio URL required"); return; }
    try {
      if (editingMusic === "new") {
        await api.post("/admin/music", musicForm);
      } else {
        await api.put(`/admin/music/${editingMusic}`, musicForm);
      }
      toast.success("Saved");
      setEditingMusic(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deleteMusic = async (id) => {
    if (!window.confirm("Remove this track?")) return;
    await api.delete(`/admin/music/${id}`);
    fetchAll();
  };

  // Swap a music track with its neighbor.
  const moveMusic = async (id, dir) => {
    const sorted = [...music].sort((a, b) => (a.position || 0) - (b.position || 0));
    const i = sorted.findIndex((t) => t.id === id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= sorted.length) return;
    const a = sorted[i];
    const b = sorted[j];
    try {
      await Promise.all([
        api.put(`/admin/music/${a.id}`, { ...a, position: b.position }),
        api.put(`/admin/music/${b.id}`, { ...b, position: a.position }),
      ]);
      fetchAll();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  // ---------- gallery ----------
  const startEditPhoto = (p) => { setEditingPhoto(p.id); setPhotoForm({ ...BLANK_PHOTO, ...p, artists: Array.isArray(p.artists) ? p.artists : [] }); };
  const newPhoto = () => { setEditingPhoto("new"); setPhotoForm({ ...BLANK_PHOTO, position: photos.length + 1 }); };
  const savePhoto = async () => {
    if (!photoForm.image_path) { toast.error("Upload an image first"); return; }
    try {
      if (editingPhoto === "new") {
        await api.post("/admin/gallery", photoForm);
      } else {
        await api.put(`/admin/gallery/${editingPhoto}`, photoForm);
      }
      toast.success("Saved");
      setEditingPhoto(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    await api.delete(`/admin/gallery/${id}`);
    fetchAll();
  };

  // Bulk upload multiple photos to a chosen section
  const [bulkSection, setBulkSection] = useState(1);
  const [bulkProgress, setBulkProgress] = useState(null); // null | {done, total, failed}

  const bulkUploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const section = bulkSection;
    // Starting position = max position currently in that section + 1
    const inSection = photos.filter((p) => (p.section || 1) === section);
    let nextPos = inSection.reduce((m, p) => Math.max(m, p.position || 0), 0) + 1;
    setBulkProgress({ done: 0, total: files.length, failed: 0 });
    let done = 0;
    let failed = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "image");
        const up = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/admin/gallery", {
          image_path: up.data.storage_path,
          caption: "",
          position: nextPos,
          section,
          active: true,
        });
        nextPos += 1;
      } catch {
        failed += 1;
      }
      done += 1;
      setBulkProgress({ done, total: files.length, failed });
    }
    if (failed === 0) toast.success(`Uploaded ${done} photos`);
    else toast.error(`Uploaded ${done - failed} / ${done}. ${failed} failed.`);
    await fetchAll();
    // Auto-clear progress after a short moment
    setTimeout(() => setBulkProgress(null), 2000);
  };

  const saveSectionName = async (position, name) => {
    try {
      await api.put(`/admin/gallery/sections/${position}`, { name });
      toast.success(`Section ${position} renamed`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const quickSetPhotoSection = async (photo, section) => {
    try {
      await api.put(`/admin/gallery/${photo.id}`, { ...photo, section });
      toast.success("Section updated");
      fetchAll();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  // Swap a gallery photo with its neighbor — within the same section only.
  const movePhoto = async (id, dir) => {
    const current = photos.find((p) => p.id === id);
    if (!current) return;
    const sectionPhotos = photos
      .filter((p) => (p.section || 1) === (current.section || 1))
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    const i = sectionPhotos.findIndex((p) => p.id === id);
    const j = dir === "up" ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= sectionPhotos.length) return;
    const a = sectionPhotos[i];
    const b = sectionPhotos[j];
    try {
      await Promise.all([
        api.put(`/admin/gallery/${a.id}`, { ...a, position: b.position }),
        api.put(`/admin/gallery/${b.id}`, { ...b, position: a.position }),
      ]);
      fetchAll();
    } catch (e) {
      toast.error("Reorder failed");
    }
  };

  // ---------- gaming ----------
  const saveGamingConfig = async () => {
    try {
      await api.put("/admin/gaming/config", {
        discord_invite_url: gamingConfig.discord_invite_url || "",
        hero_youtube_id: gamingConfig.hero_youtube_id || "",
      });
      toast.success("Saved");
      fetchAll();
    } catch {
      toast.error("Save failed");
    }
  };

  const startEditStreamer = (s) => { setEditingStreamer(s.id); setStreamerForm({ ...BLANK_STREAMER, ...s }); };
  const newStreamer = () => { setEditingStreamer("new"); setStreamerForm(BLANK_STREAMER); };
  const saveStreamer = async () => {
    if (!streamerForm.name || !streamerForm.handle) return toast.error("Name and handle required");
    try {
      if (editingStreamer === "new") await api.post("/admin/gaming/streamers", streamerForm);
      else await api.put(`/admin/gaming/streamers/${editingStreamer}`, streamerForm);
      toast.success("Saved");
      setEditingStreamer(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteStreamer = async (id) => {
    if (!window.confirm("Remove streamer?")) return;
    await api.delete(`/admin/gaming/streamers/${id}`);
    fetchAll();
  };

  const startEditClip = (c) => { setEditingClip(c.id); setClipForm({ ...BLANK_CLIP, ...c }); };
  const newClip = () => { setEditingClip("new"); setClipForm(BLANK_CLIP); };
  const saveClip = async () => {
    if (!clipForm.youtube_id) return toast.error("YouTube ID/URL required");
    try {
      if (editingClip === "new") await api.post("/admin/gaming/clips", clipForm);
      else await api.put(`/admin/gaming/clips/${editingClip}`, clipForm);
      toast.success("Saved");
      setEditingClip(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteClip = async (id) => {
    if (!window.confirm("Delete clip?")) return;
    await api.delete(`/admin/gaming/clips/${id}`);
    fetchAll();
  };

  // ---------- twitch vods ----------
  const startEditVod = (v) => { setEditingVod(v.id); setVodForm({ ...BLANK_VOD, ...v }); };
  const newVod = () => { setEditingVod("new"); setVodForm(BLANK_VOD); };
  const saveVod = async () => {
    if (!vodForm.video_id) return toast.error("Twitch VOD URL/ID required");
    try {
      if (editingVod === "new") await api.post("/admin/gaming/twitch-vods", vodForm);
      else await api.put(`/admin/gaming/twitch-vods/${editingVod}`, vodForm);
      toast.success("Saved");
      setEditingVod(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteVod = async (id) => {
    if (!window.confirm("Delete VOD?")) return;
    await api.delete(`/admin/gaming/twitch-vods/${id}`);
    fetchAll();
  };

  const startEditSquad = (m) => {
    setEditingSquadMember(m.id);
    setSquadForm({ ...BLANK_SQUAD, ...m, favorite_games: m.favorite_games || [] });
  };
  const newSquadMember = () => { setEditingSquadMember("new"); setSquadForm(BLANK_SQUAD); };
  const saveSquadMember = async () => {
    if (!squadForm.name) return toast.error("Name required");
    try {
      if (editingSquadMember === "new") await api.post("/admin/gaming/squad", squadForm);
      else await api.put(`/admin/gaming/squad/${editingSquadMember}`, squadForm);
      toast.success("Saved");
      setEditingSquadMember(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteSquadMember = async (id) => {
    if (!window.confirm("Remove squad member?")) return;
    await api.delete(`/admin/gaming/squad/${id}`);
    fetchAll();
  };

  // ---------- artists ----------
  const startEditArtist = (a) => { setEditingArtist(a.id); setArtistForm({ ...BLANK_ARTIST, ...a }); };
  const newArtist = () => { setEditingArtist("new"); setArtistForm(BLANK_ARTIST); };
  const saveArtist = async () => {
    if (!artistForm.name) return toast.error("Name required");
    try {
      if (editingArtist === "new") await api.post("/admin/artists", artistForm);
      else await api.put(`/admin/artists/${editingArtist}`, artistForm);
      toast.success("Saved");
      setEditingArtist(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteArtist = async (id) => {
    if (!window.confirm("Remove artist?")) return;
    await api.delete(`/admin/artists/${id}`);
    fetchAll();
  };

  const syncArtistsFromTracks = async () => {
    if (!window.confirm("Scan the Music catalog and add any missing artists to the roster?")) return;
    try {
      const res = await api.post("/admin/artists/sync-from-tracks");
      const { added, total_unique_in_tracks } = res.data || {};
      if (added > 0) {
        toast.success(`Added ${added} new artist${added === 1 ? "" : "s"} (scanned ${total_unique_in_tracks} unique names).`);
      } else {
        toast.success(`Roster already up to date (${total_unique_in_tracks} unique names in tracks).`);
      }
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    }
  };

  // ---------- feature videos ----------
  const startEditFeatureVideo = (v) => { setEditingFeatureVideo(v.id); setFeatureVideoForm({ ...BLANK_FEATURE_VIDEO, ...v }); };
  const newFeatureVideo = () => { setEditingFeatureVideo("new"); setFeatureVideoForm(BLANK_FEATURE_VIDEO); };
  const saveFeatureVideo = async () => {
    try {
      if (editingFeatureVideo === "new") await api.post("/admin/feature-videos", featureVideoForm);
      else await api.put(`/admin/feature-videos/${editingFeatureVideo}`, featureVideoForm);
      toast.success("Saved");
      setEditingFeatureVideo(null);
      fetchAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const deleteFeatureVideo = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    await api.delete(`/admin/feature-videos/${id}`);
    fetchAll();
  };

  // ---------- home config ----------
  const saveHomeConfig = async () => {
    try {
      await api.put("/admin/home/config", homeConfig || {});
      toast.success("Home page updated");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const updateHomeField = (key, value) => setHomeConfig({ ...(homeConfig || {}), [key]: value });
  const updateMarqueeItem = (idx, value) => {
    const items = [...(homeConfig?.marquee_items || [])];
    items[idx] = value;
    setHomeConfig({ ...(homeConfig || {}), marquee_items: items });
  };
  const addMarqueeItem = () => {
    const items = [...(homeConfig?.marquee_items || []), ""];
    setHomeConfig({ ...(homeConfig || {}), marquee_items: items });
  };
  const removeMarqueeItem = (idx) => {
    const items = (homeConfig?.marquee_items || []).filter((_, i) => i !== idx);
    setHomeConfig({ ...(homeConfig || {}), marquee_items: items });
  };

  // ---------- admin users ----------
  const createAdmin = async () => {
    if (!newAdminForm.username || !newAdminForm.password) {
      toast.error("Username and password required");
      return;
    }
    if (newAdminForm.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    try {
      await api.post("/admin/users", newAdminForm);
      toast.success("Admin created");
      setNewAdminForm(BLANK_ADMIN);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };
  const removeAdmin = async (id) => {
    if (!window.confirm("Revoke admin access from this user?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  // ---------- creators (content contributor role) ----------
  const createCreator = async () => {
    if (!newCreatorForm.username || !newCreatorForm.password) {
      toast.error("Username and password required"); return;
    }
    if (newCreatorForm.password.length < 8) {
      toast.error("Password must be at least 8 characters"); return;
    }
    try {
      await api.post("/admin/creators", newCreatorForm);
      toast.success(`Creator ${newCreatorForm.username} added`);
      setNewCreatorForm({ username: "", password: "", name: "" });
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };
  const deleteCreator = async (id, username) => {
    if (!window.confirm(`Delete creator "${username}"? Their uploads remain in the library but they will be signed out immediately and cannot sign back in.`)) return;
    try {
      await api.delete(`/admin/creators/${id}`);
      toast.success("Creator deleted");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const resetCreatorPassword = async (id, newPass) => {
    if (!newPass || newPass.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.put(`/admin/creators/${id}/password`, { password: newPass });
      toast.success("Password reset — creator has been signed out of all devices");
      setCreatorPwPrompt(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };
  const toggleCreatorUploads = async (id) => {
    if (expandedCreator === id) { setExpandedCreator(null); return; }
    setExpandedCreator(id);
    if (!creatorUploads[id]) {
      try {
        const { data } = await api.get(`/admin/creators/${id}/uploads`);
        setCreatorUploads((cur) => ({ ...cur, [id]: data || { tracks: [], photos: [] } }));
      } catch (e) {
        toast.error("Could not load uploads");
      }
    }
  };

  // ---------- render ----------
  if (authLoading) return <div className="px-5 py-20 font-mono text-white/50">LOADING...</div>;

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-[500px] mx-auto px-5 py-20" data-testid="admin-login-page">
        <Seo title="Admin | SyndaFam" description="SyndaFam control room." noindex />
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-2">// CONTROL ROOM</div>
        <h1 className="font-display text-5xl mb-1">ADMIN ACCESS.</h1>
        <p className="text-white/50 text-sm mb-8">Signed-in only. Identify yourself.</p>

        <form onSubmit={handlePasswordLogin} className="bg-[#151515] border border-white/10 p-6 space-y-4" data-testid="admin-password-form">
          <div className="font-mono text-[10px] tracking-widest text-white/50 flex items-center gap-2">
            <KeyRound className="w-3 h-3" /> USERNAME + PASSWORD
          </div>
          <input
            value={loginForm.username}
            onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
            placeholder="USERNAME"
            autoComplete="username"
            className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 focus:outline-none focus:border-[#E63946]"
            data-testid="admin-username-input"
          />
          <input
            value={loginForm.password}
            onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
            type="password"
            placeholder="PASSWORD"
            autoComplete="current-password"
            className="w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 focus:outline-none focus:border-[#E63946]"
            data-testid="admin-password-input"
          />
          <button
            type="submit"
            disabled={loginLoading}
            className="w-full font-display tracking-wide text-lg bg-[#E63946] hover:bg-[#C92A36] disabled:opacity-50 text-white px-6 py-3"
            data-testid="admin-password-submit"
          >
            {loginLoading ? "SIGNING IN..." : "SIGN IN"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-white/10" />
          <div className="font-mono text-[10px] tracking-widest text-white/40">OR</div>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <button
          onClick={() => startGoogleLogin("/admin")}
          className="w-full font-display text-lg border border-white/30 hover:bg-white hover:text-black px-6 py-3 transition-colors"
          data-testid="admin-google-signin"
        >
          CONTINUE WITH GOOGLE
        </button>

        {user && user.role !== "admin" && (
          <div className="mt-6 font-mono text-xs text-white/50" data-testid="admin-forbidden">
            Signed in as <span className="text-white">{user.email || user.username}</span> — not an admin.
          </div>
        )}
      </div>
    );
  }

  const mediaTypesSource = (playerDraft?.media_types && playerDraft.media_types.length)
    ? playerDraft.media_types
    : (playerConfig?.media_types || []);

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10" data-testid="admin-page">
      <Seo title="Admin | SyndaFam" description="SyndaFam control room." noindex />
      <div className="flex justify-between items-end mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-2">// CONTROL ROOM</div>
          <h1 className="font-display text-5xl">ADMIN.</h1>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => setTab("traffic")}
            className="glass-card rounded-2xl px-5 py-3 text-left hover:border-white/25 transition-colors group"
            title="Open TRAFFIC tab for full stats"
            data-testid="admin-header-traffic-90"
          >
            <div className="font-mono text-[9px] tracking-[0.3em] text-white/40 group-hover:text-white/70">// LAST 90 DAYS</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div>
                <div className="font-display text-2xl leading-none text-[#FCA311]" data-testid="admin-header-visitors-value">
                  {traffic90 ? traffic90.unique_visitors.toLocaleString() : "—"}
                </div>
                <div className="font-mono text-[9px] tracking-widest text-white/40 mt-1">VISITORS</div>
              </div>
              <div className="w-px h-8 bg-white/10" />
              <div>
                <div className="font-display text-2xl leading-none text-[#E63946]" data-testid="admin-header-views-value">
                  {traffic90 ? traffic90.total_views.toLocaleString() : "—"}
                </div>
                <div className="font-mono text-[9px] tracking-widest text-white/40 mt-1">VIEWS</div>
              </div>
            </div>
          </button>
          <div className="font-mono text-xs text-white/40">{user.username || user.email}</div>
        </div>
      </div>

      <div className="flex flex-wrap border border-white/10 mb-6 overflow-x-auto">
        {["home", "about", "player", "playlist", "artists", "reel", "gallery", "gaming", "games", "traffic", "contacts", "users"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`font-display px-5 py-3 text-sm transition-colors ${tab === t ? "bg-[#E63946] text-white" : "text-white/60 hover:text-white"}`}
            data-testid={`admin-tab-${t}`}
          >
            {t === "playlist" ? "LIBRARY" : t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "home" && (
        <div className="space-y-8" data-testid="admin-home">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display text-2xl">HOME LANDING PAGE</h3>
            <button onClick={saveHomeConfig} className="font-mono text-xs px-4 py-2 bg-[#E63946] text-white" data-testid="admin-save-home">SAVE ALL CHANGES</button>
          </div>

          {/* HERO */}
          <div className="bg-[#151515] border border-white/10 p-5 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-1">HERO</div>
            <div>
              <Label>HERO BACKGROUND IMAGE</Label>
              {homeConfig?.hero_image_path && (
                <div className="mb-2 w-full max-w-md h-40 border border-white/20 overflow-hidden">
                  <img src={imgUrl(homeConfig.hero_image_path)} alt="Hero preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD NEW HERO
                  <input type="file" accept="image/*" className="hidden" data-testid="admin-home-hero-upload" onChange={(e) => {
                    if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => updateHomeField("hero_image_path", d.storage_path));
                  }} />
                </label>
                <button
                  onClick={() => updateHomeField("hero_image_path", "/hero.jpg")}
                  className="font-mono text-[10px] px-3 py-2 border border-white/20 hover:border-[#E63946]"
                  data-testid="admin-home-hero-reset"
                >
                  RESET TO DEFAULT
                </button>
                <div className="font-mono text-[10px] text-white/40 truncate max-w-[260px]">
                  {homeConfig?.hero_image_path || "—"}
                </div>
              </div>
              <div className="font-mono text-[10px] text-white/40 mt-1">Click SAVE ALL CHANGES below to apply.</div>
            </div>
            <div>
              <Label>OVERLINE (small mono text above headline)</Label>
              <input value={homeConfig?.hero_overline || ""} onChange={(e) => updateHomeField("hero_overline", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>HEADLINE LINE 1</Label>
                <input value={homeConfig?.hero_line_1 || ""} onChange={(e) => updateHomeField("hero_line_1", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div>
                <Label>HEADLINE LINE 2 (red)</Label>
                <input value={homeConfig?.hero_line_2 || ""} onChange={(e) => updateHomeField("hero_line_2", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div>
                <Label>HEADLINE LINE 3</Label>
                <input value={homeConfig?.hero_line_3 || ""} onChange={(e) => updateHomeField("hero_line_3", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
            </div>
            <div>
              <Label>GRAFFITI SUBTITLE (yellow handwritten)</Label>
              <input value={homeConfig?.hero_graffiti || ""} onChange={(e) => updateHomeField("hero_graffiti", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <div>
              <Label>SUBTITLE PARAGRAPH</Label>
              <textarea rows={3} value={homeConfig?.hero_subtitle || ""} onChange={(e) => updateHomeField("hero_subtitle", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
          </div>

          {/* MARQUEE */}
          <div className="bg-[#151515] border border-white/10 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[10px] tracking-widest text-[#FCA311]">MARQUEE STRIP (scrolling text under hero)</div>
              <button onClick={addMarqueeItem} className="font-mono text-[10px] px-2 py-1 border border-white/20 hover:border-[#E63946]" data-testid="admin-add-marquee">+ ADD</button>
            </div>
            {(homeConfig?.marquee_items || []).map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <input value={item} onChange={(e) => updateMarqueeItem(idx, e.target.value)} className="flex-1 bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm" />
                <button onClick={() => removeMarqueeItem(idx)} className="px-3 border border-white/20 hover:border-[#E63946]" aria-label="Remove">×</button>
              </div>
            ))}
            {(homeConfig?.marquee_items || []).length === 0 && (
              <div className="font-mono text-[10px] text-white/40">No marquee items.</div>
            )}
          </div>

          {/* MANIFESTO */}
          <div className="bg-[#151515] border border-white/10 p-5 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-1">MANIFESTO</div>
            <div>
              <Label>OVERLINE</Label>
              <input value={homeConfig?.manifesto_overline || ""} onChange={(e) => updateHomeField("manifesto_overline", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>HEADING LINE 1</Label>
                <input value={homeConfig?.manifesto_heading_line_1 || ""} onChange={(e) => updateHomeField("manifesto_heading_line_1", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div>
                <Label>HEADING LINE 2</Label>
                <input value={homeConfig?.manifesto_heading_line_2 || ""} onChange={(e) => updateHomeField("manifesto_heading_line_2", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
            </div>
            <div>
              <Label>PARAGRAPH 1</Label>
              <textarea rows={3} value={homeConfig?.manifesto_body_1 || ""} onChange={(e) => updateHomeField("manifesto_body_1", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <div>
              <Label>PARAGRAPH 2 (graffiti phrase appears at end)</Label>
              <textarea rows={3} value={homeConfig?.manifesto_body_2 || ""} onChange={(e) => updateHomeField("manifesto_body_2", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <div>
              <Label>GRAFFITI PHRASE</Label>
              <input value={homeConfig?.manifesto_graffiti || ""} onChange={(e) => updateHomeField("manifesto_graffiti", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <div className="font-mono text-[10px] tracking-widest text-white/50 pt-3">STATS</div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((n) => (
                <div key={n} className="space-y-2">
                  <input placeholder="value" value={homeConfig?.[`stat_${n}_value`] || ""} onChange={(e) => updateHomeField(`stat_${n}_value`, e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-display text-2xl text-center" />
                  <input placeholder="label" value={homeConfig?.[`stat_${n}_label`] || ""} onChange={(e) => updateHomeField(`stat_${n}_label`, e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-xs text-center" />
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#151515] border border-white/10 p-5 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-1">CTA STRIP (red bar at bottom)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>OVERLINE</Label>
                <input value={homeConfig?.cta_overline || ""} onChange={(e) => updateHomeField("cta_overline", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm" />
              </div>
              <div>
                <Label>TITLE</Label>
                <input value={homeConfig?.cta_title || ""} onChange={(e) => updateHomeField("cta_title", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
            </div>
            <div>
              <Label>SUBTITLE</Label>
              <input value={homeConfig?.cta_subtitle || ""} onChange={(e) => updateHomeField("cta_subtitle", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <div>
              <Label>BUTTON TEXT</Label>
              <input value={homeConfig?.cta_button_text || ""} onChange={(e) => updateHomeField("cta_button_text", e.target.value)} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={saveHomeConfig} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-home-bottom">SAVE ALL CHANGES</button>
          </div>
        </div>
      )}

      {tab === "videos" && (
        <div>
          <button onClick={newVideo} className="mb-4 font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-video">
            <Plus className="w-4 h-4" /> NEW VIDEO
          </button>
          {editingVideo && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6" data-testid="admin-video-edit">
              <div className="font-display text-2xl mb-4">{editingVideo === "new" ? "NEW VIDEO" : "EDIT VIDEO"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>YOUTUBE ID OR URL</Label>
                  <input placeholder="dQw4w9WgXcQ or https://youtu.be/..." value={videoForm.youtube_id} onChange={(e) => setVideoForm({ ...videoForm, youtube_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-video-id" />
                </div>
                <div>
                  <Label>TITLE (optional)</Label>
                  <input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={videoForm.position} onChange={(e) => setVideoForm({ ...videoForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={saveVideo} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-video">SAVE</button>
                <button onClick={() => setEditingVideo(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-videos-list">
            {videos.map((v) => (
              <div key={v.id} className="bg-[#151515] border border-white/10 p-3">
                <div className="aspect-video bg-black mb-2">
                  <img src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="font-mono text-[10px] text-white/50">#{v.position} · {v.youtube_id}</div>
                <div className="font-display text-base">{v.title || "Untitled"}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => startEditVideo(v)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> EDIT</button>
                  <button onClick={() => deleteVideo(v.id)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1" data-testid={`admin-delete-video-${v.id}`}><Trash2 className="w-3 h-3" /> DELETE</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "playlist" && (
        <div>
          <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
            <div>
              <h2 className="font-display text-3xl">LIBRARY</h2>
              <p className="text-white/50 text-sm mt-1 max-w-2xl">
                Every song, video, instrumental and podcast in one place. Tap the section pills on each row to assign that track to any player section instantly — no need to open the editor.
              </p>
            </div>
            <button onClick={newTrack} className="font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-track">
              <Plus className="w-4 h-4" /> NEW TRACK
            </button>
          </div>
          {editingTrack && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6" data-testid="admin-track-edit">
              <div className="font-display text-2xl mb-4">{editingTrack === "new" ? "NEW TRACK" : "EDIT TRACK"}</div>
              <p className="font-mono text-[10px] text-white/50 mb-4">
                Add either a YouTube ID/URL <strong className="text-white/70">OR</strong> an audio file (Dropbox raw URL / uploaded mp3). Audio takes priority on the player if both are provided.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>YOUTUBE ID OR URL (optional)</Label>
                  <input placeholder="dQw4w9WgXcQ or https://youtu.be/..." value={trackForm.youtube_id} onChange={(e) => setTrackForm({ ...trackForm, youtube_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-id" />
                </div>
                <div className="md:col-span-2">
                  <Label>AUDIO URL (Dropbox raw=1 / uploaded mp3)</Label>
                  <div className="flex gap-2">
                    <input placeholder="https://www.dropbox.com/...&raw=1" value={trackForm.audio_url} onChange={(e) => setTrackForm({ ...trackForm, audio_url: e.target.value })} className="flex-1 bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-audio-url" />
                    <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD
                      <input type="file" accept="audio/*" className="hidden" data-testid="admin-track-audio-upload" onChange={(e) => {
                        if (e.target.files?.[0]) uploadFile(e.target.files[0], "audio", (d) => setTrackForm((cur) => ({ ...cur, audio_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.storage_path}` })));
                      }} />
                    </label>
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-white/40">
                    Dropbox tip: replace <code className="text-[#FCA311]">dl=0</code> at the end of the share link with <code className="text-[#FCA311]">raw=1</code>.
                  </div>
                </div>
                <div>
                  <Label>TRACK TITLE</Label>
                  <input value={trackForm.title} onChange={(e) => setTrackForm({ ...trackForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-title" />
                </div>
                <div>
                  <Label>ARTIST(S)</Label>
                  <input value={trackForm.artist} onChange={(e) => setTrackForm({ ...trackForm, artist: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-artist" placeholder="Shao, Elaztic feat. Jynx" />
                  <p className="font-mono text-[10px] text-white/40 mt-1 leading-relaxed">
                    Multiple artists — separate with <code className="text-[#FCA311]">,</code> <code className="text-[#FCA311]">&amp;</code> <code className="text-[#FCA311]">feat.</code> <code className="text-[#FCA311]">ft.</code> <code className="text-[#FCA311]">with</code> <code className="text-[#FCA311]">x</code>. Each artist becomes its own glowing chip on the player and links to their archive card.
                  </p>
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={trackForm.position} onChange={(e) => setTrackForm({ ...trackForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <Label>PLAYER SECTIONS (choose one or more — this track shows up in each checked pill)</Label>
                  <div className="flex flex-wrap gap-2" data-testid="admin-track-media-types">
                    {(mediaTypesSource.length ? mediaTypesSource : [{ key: "audio", label: "AUDIO", color: "#FCA311" }, { key: "video", label: "VIDEO", color: "#E63946" }, { key: "instrumental", label: "INSTRUMENTAL", color: "#67E8F9" }, { key: "podcast", label: "PODCAST", color: "#A78BFA" }]).map((mt) => {
                      const selected = (trackForm.media_types || [trackForm.media_type || "audio"]).includes(mt.key);
                      return (
                        <button
                          type="button"
                          key={mt.key}
                          onClick={() => {
                            const cur = trackForm.media_types && trackForm.media_types.length ? [...trackForm.media_types] : [trackForm.media_type || "audio"];
                            const idx = cur.indexOf(mt.key);
                            let next;
                            if (idx >= 0) {
                              next = cur.filter((k) => k !== mt.key);
                              if (next.length === 0) next = [mt.key]; // never allow zero — reselect
                            } else {
                              next = [...cur, mt.key];
                            }
                            setTrackForm({ ...trackForm, media_types: next, media_type: next[0] });
                          }}
                          className="font-mono text-[11px] tracking-widest px-3 py-2 border-2 transition-colors"
                          style={{
                            borderColor: selected ? mt.color : "rgba(255,255,255,0.15)",
                            background: selected ? mt.color : "transparent",
                            color: selected ? "#0A0A0A" : "rgba(255,255,255,0.7)",
                          }}
                          data-testid={`admin-track-media-type-${mt.key}`}
                        >
                          {selected ? "✓ " : "+ "}{mt.label || mt.key.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                  <p className="font-mono text-[10px] text-white/40 mt-1">
                    A song can live in multiple sections at once — e.g. an instrumental beat can appear under both INSTRUMENTAL and AUDIO.
                  </p>
                </div>
                <div>
                  <Label>GENRE (free text — e.g. HIP-HOP, R&amp;B, TRAP)</Label>
                  <input value={trackForm.genre || ""} onChange={(e) => setTrackForm({ ...trackForm, genre: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-genre" />
                </div>
                <div>
                  <Label>PROFESSION</Label>
                  <select value={trackForm.profession || ""} onChange={(e) => setTrackForm({ ...trackForm, profession: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-profession">
                    <option value="">— none —</option>
                    <option value="audio-engineer">AUDIO ENGINEER</option>
                    <option value="rapper">RAPPER</option>
                    <option value="singer">SINGER</option>
                    <option value="producer">PRODUCER</option>
                    <option value="songwriter">SONGWRITER</option>
                    <option value="dj">DJ</option>
                  </select>
                </div>
                <div className="sm:col-span-3">
                  <Label>ARTWORK URL (optional — square image works best)</Label>
                  <div className="flex gap-2">
                    <input value={trackForm.artwork_url || ""} onChange={(e) => setTrackForm({ ...trackForm, artwork_url: e.target.value })} placeholder="/about/… or full URL" className="flex-1 bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-track-artwork" />
                    <label className="cursor-pointer font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311]">
                      UPLOAD
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setTrackForm((cur) => ({ ...cur, artwork_url: d.storage_path }))); }} />
                    </label>
                  </div>
                  {trackForm.artwork_url && (
                    <img src={imgUrl(trackForm.artwork_url)} alt="" className="mt-2 max-h-24 border border-white/10 object-cover" />
                  )}
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={saveTrack} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-track">SAVE</button>
                <button onClick={() => setEditingTrack(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}
          <div className="bg-[#151515] border border-white/10" data-testid="admin-tracks-list">
            {/* Filter toolbar */}
            <div className="p-3 border-b border-white/5 flex flex-wrap items-center gap-2 sticky top-0 bg-[#151515] z-10" data-testid="library-toolbar">
              <input
                value={librarySearch}
                onChange={(e) => setLibrarySearch(e.target.value)}
                placeholder="Search title, artist, genre…"
                className="flex-1 min-w-[220px] bg-black border border-white/15 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs"
                data-testid="library-search"
              />
              <button
                onClick={() => setLibraryFilterType("")}
                className={`font-mono text-[10px] tracking-widest px-3 py-2 border transition-colors ${!libraryFilterType ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:border-white"}`}
                data-testid="library-filter-all"
              >
                ALL ({tracks.length})
              </button>
              {mediaTypesSource.map((mt) => {
                const count = tracks.filter((tr) => {
                  const types = Array.isArray(tr.media_types) && tr.media_types.length ? tr.media_types : [tr.media_type || "audio"];
                  return types.includes(mt.key);
                }).length;
                const active = libraryFilterType === mt.key;
                return (
                  <button
                    key={mt.key}
                    onClick={() => setLibraryFilterType(active ? "" : mt.key)}
                    className="font-mono text-[10px] tracking-widest px-3 py-2 border-2 transition-colors"
                    style={{
                      background: active ? mt.color : "transparent",
                      borderColor: active ? mt.color : `${mt.color}55`,
                      color: active ? "#0A0A0A" : mt.color,
                    }}
                    data-testid={`library-filter-${mt.key}`}
                  >
                    {mt.label || mt.key.toUpperCase()} ({count})
                  </button>
                );
              })}
              {/* Uploader filter — quickly find items by who uploaded them
                  (admin or creator). Handy for both spot-checking a new
                  contributor and finding your own recent uploads. */}
              {(() => {
                const creatorTrackCount = tracks.filter((t) => t.uploaded_by_role === "creator" || (t.uploaded_by && !t.uploaded_by_role)).length;
                const adminTrackCount = tracks.filter((t) => t.uploaded_by_role === "admin").length;
                const seedCount = tracks.filter((t) => !t.uploaded_by).length;
                return (
                  <select
                    value={libraryFilterCreator}
                    onChange={(e) => setLibraryFilterCreator(e.target.value)}
                    className="font-mono text-[10px] tracking-widest px-3 py-2 bg-black border-2 border-[#FCA311]/55 text-[#FCA311] focus:border-[#FCA311] outline-none"
                    data-testid="library-filter-creator"
                    title="Filter by uploader"
                  >
                    <option value="">UPLOADER: ALL</option>
                    <option value="creators-any">— ANY CREATOR ({creatorTrackCount}) —</option>
                    <option value="admins-any">— ANY ADMIN ({adminTrackCount}) —</option>
                    {seedCount > 0 && <option value="seed">— NO UPLOADER / SEED ({seedCount}) —</option>}
                    {creators.length > 0 && <option disabled>── CREATORS ──</option>}
                    {creators.map((c) => (
                      <option key={c.user_id} value={c.user_id}>
                        @{c.username} ({c.track_count || 0})
                      </option>
                    ))}
                    {users.length > 0 && <option disabled>── ADMINS ──</option>}
                    {users.map((u) => {
                      const count = tracks.filter((tr) => tr.uploaded_by === u.user_id).length;
                      return (
                        <option key={u.user_id} value={u.user_id}>
                          @{u.username || u.name} ({count})
                        </option>
                      );
                    })}
                  </select>
                );
              })()}
            </div>
            {tracks.length === 0 && <div className="p-6 text-white/40 font-mono text-sm">No tracks yet. Add one above.</div>}
            {(() => {
              const q = librarySearch.trim().toLowerCase();
              const visible = tracks.filter((t) => {
                if (libraryFilterType) {
                  const types = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
                  if (!types.includes(libraryFilterType)) return false;
                }
                if (libraryFilterCreator) {
                  if (libraryFilterCreator === "creators-any") {
                    // Any creator upload — recognises role tag or (legacy) missing role but present uploaded_by from a creator.
                    if (!(t.uploaded_by_role === "creator" || (t.uploaded_by && !t.uploaded_by_role))) return false;
                  } else if (libraryFilterCreator === "admins-any") {
                    if (t.uploaded_by_role !== "admin") return false;
                  } else if (libraryFilterCreator === "seed") {
                    if (t.uploaded_by) return false;
                  } else {
                    // Specific user_id (either creator or admin)
                    if (t.uploaded_by !== libraryFilterCreator) return false;
                  }
                }
                if (q) {
                  const hay = `${t.title || ""} ${t.artist || ""} ${t.genre || ""} ${t.uploaded_by_username || ""}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              if (visible.length === 0 && tracks.length > 0) {
                return <div className="p-6 text-white/40 font-mono text-sm" data-testid="library-empty-filter">No tracks match the current filter.</div>;
              }
              return visible.map((t, idx) => (
              <div key={t.id} className="flex items-start gap-3 px-4 py-3 border-b border-white/5" data-testid={`library-row-${t.id}`}>
                <div className="flex flex-col gap-0.5 pt-1">
                  <button
                    onClick={() => moveTrack(t.id, "up")}
                    disabled={idx === 0}
                    className="p-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                    data-testid={`admin-track-up-${t.id}`}
                    aria-label="Move up"
                  >
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => moveTrack(t.id, "down")}
                    disabled={idx === tracks.length - 1}
                    className="p-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                    data-testid={`admin-track-down-${t.id}`}
                    aria-label="Move down"
                  >
                    <ArrowDown className="w-3 h-3" />
                  </button>
                </div>
                <div className="font-mono text-xs text-white/40 w-8">{String(t.position).padStart(2, "0")}</div>
                <div className="w-14 h-10 bg-black flex-shrink-0 flex items-center justify-center">
                  {t.youtube_id ? (
                    <img src={`https://img.youtube.com/vi/${t.youtube_id}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-[#FCA311] font-mono text-[10px]">AUDIO</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate flex items-center gap-2">
                    {t.title || "Untitled"}
                    {t.uploaded_by_username && (
                      <span
                        className="font-mono text-[9px] tracking-widest px-2 py-0.5"
                        style={{
                          background: t.uploaded_by_role === "admin" ? "#E63946" : "#FCA311",
                          color: t.uploaded_by_role === "admin" ? "#FFFFFF" : "#000000",
                        }}
                        title={`Uploaded by ${t.uploaded_by_role || "creator"} @${t.uploaded_by_username}`}
                        data-testid={`library-creator-badge-${t.id}`}
                      >
                        @{t.uploaded_by_username}
                      </span>
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-white/50 truncate">
                    {t.artist || (t.audio_url ? "audio file" : t.youtube_id)}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`library-sections-${t.id}`}>
                    {mediaTypesSource.map((mt) => {
                      const cur = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
                      const on = cur.includes(mt.key);
                      const color = mt.color || "#FCA311";
                      const label = (mt.label || mt.key).toUpperCase();
                      return (
                        <button
                          key={mt.key}
                          onClick={() => toggleTrackSection(t, mt.key)}
                          disabled={assignBusy === t.id}
                          className="font-mono text-[9px] tracking-widest px-1.5 py-0.5 border transition-colors disabled:opacity-40"
                          style={{
                            background: on ? color : "transparent",
                            borderColor: on ? color : `${color}44`,
                            color: on ? "#0A0A0A" : `${color}CC`,
                          }}
                          title={on ? `Remove from ${label}` : `Add to ${label}`}
                          data-testid={`library-section-toggle-${t.id}-${mt.key}`}
                        >
                          {on ? "✓ " : "+ "}{label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <button onClick={() => startEditTrack(t)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> EDIT</button>
                <button onClick={() => deleteTrack(t.id)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1" data-testid={`admin-delete-track-${t.id}`}><Trash2 className="w-3 h-3" /> DELETE</button>
              </div>
              ));
            })()}
          </div>
        </div>
      )}

      {tab === "music" && (
        <div data-testid="admin-music-tab">
          <button onClick={newMusic} className="mb-4 font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-music">
            <Plus className="w-4 h-4" /> NEW MUSIC TRACK
          </button>
          {editingMusic && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6" data-testid="admin-music-edit">
              <div className="font-display text-2xl mb-4">{editingMusic === "new" ? "NEW MUSIC TRACK" : "EDIT MUSIC TRACK"}</div>
              <p className="font-mono text-[10px] text-white/50 mb-4">
                For Dropbox: paste the share link and replace <code className="text-[#FCA311]">dl=0</code> at the end with <code className="text-[#FCA311]">raw=1</code>. Or upload an mp3 to object storage and paste that path.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>TRACK TITLE</Label>
                  <input value={musicForm.title} onChange={(e) => setMusicForm({ ...musicForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-music-title" />
                </div>
                <div>
                  <Label>ARTIST(S)</Label>
                  <input value={musicForm.artist} onChange={(e) => setMusicForm({ ...musicForm, artist: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-music-artist" placeholder="Shao, Elaztic feat. Jynx" />
                  <p className="font-mono text-[10px] text-white/40 mt-1 leading-relaxed">
                    Multiple artists — separate with <code className="text-[#FCA311]">,</code> <code className="text-[#FCA311]">&amp;</code> <code className="text-[#FCA311]">feat.</code> <code className="text-[#FCA311]">ft.</code> <code className="text-[#FCA311]">with</code> <code className="text-[#FCA311]">x</code>.
                  </p>
                </div>
                <div className="md:col-span-2">
                  <Label>AUDIO URL (mp3/m4a/wav)</Label>
                  <div className="flex gap-2">
                    <input value={musicForm.src} onChange={(e) => setMusicForm({ ...musicForm, src: e.target.value })} placeholder="https://...?raw=1" className="flex-1 bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-music-src" />
                    <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD
                      <input type="file" accept="audio/*" className="hidden" data-testid="admin-music-upload" onChange={(e) => {
                        if (e.target.files?.[0]) uploadFile(e.target.files[0], "audio", (d) => setMusicForm((cur) => ({ ...cur, src: `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.storage_path}` })));
                      }} />
                    </label>
                  </div>
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={musicForm.position} onChange={(e) => setMusicForm({ ...musicForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="flex items-center gap-2 self-end">
                  <input type="checkbox" id="music-active" checked={musicForm.active} onChange={(e) => setMusicForm({ ...musicForm, active: e.target.checked })} />
                  <label htmlFor="music-active" className="font-mono text-xs">ACTIVE</label>
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={saveMusic} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-music">SAVE</button>
                <button onClick={() => setEditingMusic(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}
          <div className="bg-[#151515] border border-white/10" data-testid="admin-music-list">
            <div className="px-4 py-3 border-b border-white/10 font-mono text-[10px] tracking-widest text-white/50 flex justify-between">
              <span>{music.length} TRACK{music.length !== 1 ? "S" : ""}</span>
              <span>SHOWN ON /music PAGE</span>
            </div>
            {music.length === 0 && <div className="p-6 text-white/40 font-mono text-sm">No music tracks. Add one above.</div>}
            <div className="max-h-[640px] overflow-y-auto">
              {music.map((t, idx) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5">
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => moveMusic(t.id, "up")}
                      disabled={idx === 0}
                      className="p-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                      data-testid={`admin-music-up-${t.id}`}
                      aria-label="Move up"
                    >
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveMusic(t.id, "down")}
                      disabled={idx === music.length - 1}
                      className="p-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                      data-testid={`admin-music-down-${t.id}`}
                      aria-label="Move down"
                    >
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="font-mono text-xs text-white/40 w-10 flex-shrink-0">{String(t.position).padStart(3, "0")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">{t.title || "Untitled"}</div>
                    <div className="font-mono text-[10px] text-white/50 truncate">{t.artist}</div>
                  </div>
                  <button onClick={() => startEditMusic(t)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1" data-testid={`admin-edit-music-${t.id}`}><Pencil className="w-3 h-3" /> EDIT</button>
                  <button onClick={() => deleteMusic(t.id)} className="font-mono text-xs px-3 py-1 border border-white/20 hover:border-[#E63946] inline-flex items-center gap-1" data-testid={`admin-delete-music-${t.id}`}><Trash2 className="w-3 h-3" /> DELETE</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "artists" && (
        <div data-testid="admin-artists">
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={newArtist} className="font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-artist">
              <Plus className="w-4 h-4" /> NEW ARTIST
            </button>
            <button onClick={syncArtistsFromTracks} className="font-mono text-xs px-4 py-2 border border-[#FCA311] text-[#FCA311] hover:bg-[#FCA311] hover:text-black transition-colors inline-flex items-center gap-2" data-testid="admin-sync-artists">
              <Upload className="w-4 h-4" /> AUTO-FILL FROM TRACKS
            </button>
          </div>
          {editingArtist && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-artist-edit">
              <div>
                <Label>NAME</Label>
                <input value={artistForm.name} onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-artist-name" />
              </div>
              <div>
                <Label>ROLE (Rapper, Producer, DJ, …)</Label>
                <input value={artistForm.role} onChange={(e) => setArtistForm({ ...artistForm, role: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <Label>BIO</Label>
                <textarea rows={4} value={artistForm.bio} onChange={(e) => setArtistForm({ ...artistForm, bio: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <Label>PHOTO</Label>
                {artistForm.photo_path && (
                  <div className="mb-2 w-24 h-24 border border-white/20">
                    <img src={imgUrl(artistForm.photo_path)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD PHOTO
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setArtistForm((cur) => ({ ...cur, photo_path: d.storage_path })));
                  }} />
                </label>
              </div>
              {[
                ["instagram_url", "INSTAGRAM URL"],
                ["twitter_url", "X / TWITTER URL"],
                ["youtube_url", "YOUTUBE URL"],
                ["spotify_url", "SPOTIFY URL"],
                ["apple_music_url", "APPLE MUSIC URL"],
                ["tiktok_url", "TIKTOK URL"],
                ["soundcloud_url", "SOUNDCLOUD URL"],
                ["facebook_url", "FACEBOOK URL"],
                ["website_url", "WEBSITE URL"],
              ].map(([k, lbl]) => (
                <div key={k}>
                  <Label>{lbl}</Label>
                  <input value={artistForm[k] || ""} onChange={(e) => setArtistForm({ ...artistForm, [k]: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="https://..." />
                </div>
              ))}
              <div>
                <Label>POSITION</Label>
                <input type="number" value={artistForm.position} onChange={(e) => setArtistForm({ ...artistForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div className="md:col-span-2 flex gap-2 mt-2">
                <button onClick={saveArtist} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-artist">SAVE</button>
                <button onClick={() => setEditingArtist(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-artists-list">
            {artists.map((a) => (
              <div key={a.id} className="bg-[#151515] border border-white/10 p-3 flex gap-3">
                <div className="w-16 h-16 bg-black border border-white/20 flex-shrink-0 overflow-hidden">
                  {a.photo_path ? <img src={imgUrl(a.photo_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/40 font-display text-xl">{a.name.slice(0,1).toUpperCase()}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{a.name}</div>
                  <div className="font-mono text-[10px] text-[#FCA311]">{a.role || "ARTIST"}</div>
                  <div className="font-mono text-[10px] text-white/40">#{a.position}</div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => startEditArtist(a)} className="p-1.5 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteArtist(a.id)} className="p-1.5 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-artist-${a.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {artists.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No artists yet.</div>}
          </div>
        </div>
      )}

      {tab === "reel" && (
        <div data-testid="admin-reel">
          <div className="mb-4 font-mono text-xs text-white/50">Full-screen video reel shown at /videos. Supports YouTube, uploaded mp4, or Dropbox URL.</div>
          <button onClick={newFeatureVideo} className="mb-4 font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-feature-video">
            <Plus className="w-4 h-4" /> NEW VIDEO
          </button>
          {editingFeatureVideo && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-feature-video-edit">
              <div className="md:col-span-2">
                <Label>TITLE</Label>
                <input value={featureVideoForm.title} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div className="md:col-span-2">
                <Label>DESCRIPTION (optional)</Label>
                <textarea rows={2} value={featureVideoForm.description} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, description: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>
              <div>
                <Label>SOURCE TYPE</Label>
                <select value={featureVideoForm.source_type} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, source_type: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="admin-feature-video-source">
                  <option value="youtube">YouTube</option>
                  <option value="file">Upload mp4 / direct file</option>
                  <option value="dropbox">Dropbox URL</option>
                </select>
              </div>
              <div>
                <Label>POSITION</Label>
                <input type="number" value={featureVideoForm.position} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
              </div>

              {featureVideoForm.source_type === "youtube" && (
                <div className="md:col-span-2">
                  <Label>YOUTUBE URL OR VIDEO ID</Label>
                  <input value={featureVideoForm.youtube_id} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, youtube_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="https://youtu.be/... or dQw4w9WgXcQ" />
                </div>
              )}
              {featureVideoForm.source_type === "file" && (
                <div className="md:col-span-2">
                  <Label>VIDEO FILE</Label>
                  {featureVideoForm.file_path && (
                    <div className="mb-2 font-mono text-[10px] text-white/40 truncate">{featureVideoForm.file_path}</div>
                  )}
                  <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD MP4
                    <input type="file" accept="video/mp4,video/*" className="hidden" data-testid="admin-feature-video-upload" onChange={(e) => {
                      if (e.target.files?.[0]) uploadFile(e.target.files[0], "video", (d) => setFeatureVideoForm((cur) => ({ ...cur, file_path: d.storage_path })));
                    }} />
                  </label>
                </div>
              )}
              {featureVideoForm.source_type === "dropbox" && (
                <div className="md:col-span-2">
                  <Label>DROPBOX URL (any share link — raw=1 added automatically)</Label>
                  <input value={featureVideoForm.dropbox_url} onChange={(e) => setFeatureVideoForm({ ...featureVideoForm, dropbox_url: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="https://www.dropbox.com/scl/fi/..." />
                </div>
              )}

              <div className="md:col-span-2">
                <Label>POSTER IMAGE (optional — shown before play)</Label>
                {featureVideoForm.poster_path && (
                  <div className="mb-2 w-32 h-20 border border-white/20">
                    <img src={imgUrl(featureVideoForm.poster_path)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                  <Upload className="w-3 h-3" /> UPLOAD POSTER
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setFeatureVideoForm((cur) => ({ ...cur, poster_path: d.storage_path })));
                  }} />
                </label>
              </div>

              <div className="md:col-span-2 flex gap-2">
                <button onClick={saveFeatureVideo} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-feature-video">SAVE</button>
                <button onClick={() => setEditingFeatureVideo(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-feature-videos-list">
            {featureVideos.map((v) => (
              <div key={v.id} className="bg-[#151515] border border-white/10 p-3 flex gap-3">
                <div className="w-24 h-16 bg-black border border-white/20 flex-shrink-0 overflow-hidden">
                  {v.poster_path ? (
                    <img src={imgUrl(v.poster_path)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-mono text-[10px] text-white/30">{v.source_type.toUpperCase()}</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{v.title || "(untitled)"}</div>
                  <div className="font-mono text-[10px] text-[#FCA311]">{v.source_type.toUpperCase()}</div>
                  <div className="font-mono text-[10px] text-white/40">#{v.position}</div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => startEditFeatureVideo(v)} className="p-1.5 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                  <button onClick={() => deleteFeatureVideo(v.id)} className="p-1.5 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-feature-video-${v.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            ))}
            {featureVideos.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No reel videos yet.</div>}
          </div>
        </div>
      )}

      {tab === "gallery" && (
        <div>
          {/* Section names editor */}
          <div className="bg-[#151515] border border-white/10 p-4 mb-6" data-testid="admin-section-editor">
            <div className="font-mono text-[10px] tracking-widest text-white/60 mb-3">SECTION NAMES (PUBLIC HEADERS ON /gallery)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {sections.map((s) => (
                <SectionNameEditor key={s.id} section={s} onSave={saveSectionName} />
              ))}
            </div>
          </div>

          <button onClick={newPhoto} className="mb-4 font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2" data-testid="admin-new-photo">
            <Plus className="w-4 h-4" /> NEW PHOTO
          </button>

          {/* Bulk upload */}
          <div className="bg-[#151515] border border-white/10 p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="admin-bulk-upload">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-1">BULK UPLOAD INTO SECTION</div>
              <select
                value={bulkSection}
                onChange={(e) => setBulkSection(parseInt(e.target.value))}
                className="bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-xs"
                data-testid="admin-bulk-section-select"
                disabled={!!bulkProgress}
              >
                {sections.map((s) => (
                  <option key={s.position} value={s.position}>
                    {s.position}: {s.name}
                  </option>
                ))}
              </select>
            </div>
            <label className={`font-mono text-xs px-4 py-2 border inline-flex items-center gap-2 ${bulkProgress ? "opacity-50 cursor-not-allowed border-white/10" : "border-white/30 cursor-pointer hover:border-[#E63946] bg-[#0A0A0A]"}`}>
              {bulkProgress ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {bulkProgress ? `UPLOADING ${bulkProgress.done}/${bulkProgress.total}` : "CHOOSE MULTIPLE IMAGES"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={!!bulkProgress}
                data-testid="admin-bulk-file-input"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    bulkUploadPhotos(e.target.files);
                    // reset so picking the same files again still fires onChange
                    e.target.value = "";
                  }
                }}
              />
            </label>
            {bulkProgress && bulkProgress.failed > 0 && (
              <div className="font-mono text-[10px] text-[#E63946]">{bulkProgress.failed} failed</div>
            )}
          </div>
          {editingPhoto && (
            <div className="bg-[#151515] border border-[#E63946] p-6 mb-6" data-testid="admin-photo-edit">
              <div className="font-display text-2xl mb-4">{editingPhoto === "new" ? "NEW PHOTO" : "EDIT PHOTO"}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>IMAGE</Label>
                  {photoForm.image_path && (
                    <div className="mb-2 w-40 h-40 border border-white/20">
                      <img src={imgUrl(photoForm.image_path)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD IMAGE
                    <input type="file" accept="image/*" className="hidden" data-testid="admin-photo-upload" onChange={(e) => {
                      if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setPhotoForm((cur) => ({ ...cur, image_path: d.storage_path })));
                    }} />
                  </label>
                </div>
                <div className="md:col-span-2">
                  <Label>CAPTION (optional)</Label>
                  <input value={photoForm.caption} onChange={(e) => setPhotoForm({ ...photoForm, caption: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                {/* Artist tags — multi-select. Tagged artists auto-surface
                    this photo on their `/artists/<slug>` profile via the
                    /api/gallery/by-artist/{slug} endpoint. */}
                <div className="md:col-span-2" data-testid="admin-photo-artists-picker">
                  <Label>TAG ARTISTS <span className="text-white/40 font-normal">(shows on their profile)</span></Label>
                  <div className="flex flex-wrap gap-2 p-2 bg-[#0A0A0A] border border-white/20 min-h-[52px]">
                    {artists.length === 0 && (
                      <span className="font-mono text-[10px] tracking-widest text-white/40 self-center">No artists on roster yet — add some in the ARTISTS tab first.</span>
                    )}
                    {artists.map((a) => {
                      const tagged = (photoForm.artists || []).some((n) => n.toLowerCase() === a.name.toLowerCase());
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => {
                            setPhotoForm((cur) => {
                              const list = Array.isArray(cur.artists) ? cur.artists : [];
                              const has = list.some((n) => n.toLowerCase() === a.name.toLowerCase());
                              return { ...cur, artists: has ? list.filter((n) => n.toLowerCase() !== a.name.toLowerCase()) : [...list, a.name] };
                            });
                          }}
                          className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] px-3 py-1.5 border-2 transition-all"
                          style={{
                            borderColor: tagged ? "#FCA311" : "rgba(255,255,255,0.15)",
                            background: tagged ? "#FCA311" : "transparent",
                            color: tagged ? "#0A0A0A" : "rgba(255,255,255,0.7)",
                          }}
                          data-testid={`admin-photo-artist-tag-${a.id}`}
                          aria-pressed={tagged}
                        >
                          {tagged ? "✓ " : ""}{a.name.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={photoForm.position} onChange={(e) => setPhotoForm({ ...photoForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>SECTION</Label>
                  <select
                    value={photoForm.section || 1}
                    onChange={(e) => setPhotoForm({ ...photoForm, section: parseInt(e.target.value) })}
                    className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2"
                    data-testid="admin-photo-section-select"
                  >
                    {sections.map((s) => (
                      <option key={s.position} value={s.position}>
                        {s.position} — {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex gap-2">
                <button onClick={savePhoto} className="font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="admin-save-photo">SAVE</button>
                <button onClick={() => setEditingPhoto(null)} className="font-display text-lg border border-white/30 px-6 py-3">CANCEL</button>
              </div>
            </div>
          )}
          <div className="space-y-8" data-testid="admin-photos-list">
            {sections.map((s) => {
              const sectionPhotos = photos
                .filter((p) => (p.section || 1) === s.position)
                .sort((a, b) => (a.position || 0) - (b.position || 0));
              return (
                <div key={s.id} data-testid={`admin-gallery-group-${s.position}`}>
                  <div className="flex items-end gap-3 mb-3 border-b border-white/10 pb-2">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">// S{s.position}</div>
                    <h4 className="font-display text-xl sm:text-2xl">{s.name}</h4>
                    <div className="ml-auto font-mono text-[10px] text-white/40">{sectionPhotos.length} FRAMES</div>
                  </div>
                  {sectionPhotos.length === 0 ? (
                    <div className="font-mono text-xs text-white/40 py-3">No photos in this section yet.</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {sectionPhotos.map((p, idx) => (
                        <div key={p.id} className="bg-[#151515] border border-white/10 p-2">
                          <div className="aspect-square bg-black mb-2 relative">
                            <img src={imgUrl(p.image_path)} alt="" className="w-full h-full object-cover" />
                            <div className="absolute top-1 left-1 bg-black/80 px-2 py-0.5 font-mono text-[9px] tracking-widest text-[#FCA311] border border-[#FCA311]/40">
                              S{p.section || 1}
                            </div>
                            {p.uploaded_by_username && (
                              <div
                                className="absolute top-1 right-1 bg-[#FCA311] px-2 py-0.5 font-mono text-[9px] tracking-widest text-black shadow-[0_0_10px_rgba(252,163,17,0.6)]"
                                title={`Uploaded by creator @${p.uploaded_by_username}`}
                                data-testid={`admin-photo-creator-badge-${p.id}`}
                              >
                                @{p.uploaded_by_username}
                              </div>
                            )}
                          </div>
                          <div className="font-mono text-[10px] text-white/50">#{p.position}</div>
                          {p.caption && <div className="font-mono text-xs text-white/70 truncate">{p.caption}</div>}
                          <select
                            value={p.section || 1}
                            onChange={(e) => quickSetPhotoSection(p, parseInt(e.target.value))}
                            className="w-full mt-1 bg-[#0A0A0A] border border-white/20 px-1 py-1 font-mono text-[10px]"
                            data-testid={`admin-photo-section-${p.id}`}
                          >
                            {sections.map((opt) => (
                              <option key={opt.position} value={opt.position}>
                                {opt.position}: {opt.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-1 mt-2 flex-wrap">
                            <button
                              onClick={() => movePhoto(p.id, "up")}
                              disabled={idx === 0}
                              className="font-mono text-[10px] px-2 py-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                              data-testid={`admin-photo-up-${p.id}`}
                              aria-label="Move up within section"
                            >
                              <ArrowUp className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => movePhoto(p.id, "down")}
                              disabled={idx === sectionPhotos.length - 1}
                              className="font-mono text-[10px] px-2 py-1 border border-white/20 disabled:opacity-30 hover:border-[#E63946]"
                              data-testid={`admin-photo-down-${p.id}`}
                              aria-label="Move down within section"
                            >
                              <ArrowDown className="w-3 h-3" />
                            </button>
                            <button onClick={() => startEditPhoto(p)} className="font-mono text-[10px] px-2 py-1 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                            <button onClick={() => deletePhoto(p.id)} className="font-mono text-[10px] px-2 py-1 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-photo-${p.id}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "gaming" && (
        <div className="space-y-10" data-testid="admin-gaming">
          {/* Config */}
          <div className="bg-[#151515] border border-white/10 p-5 space-y-4">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-2">HERO VIDEO (auto-plays muted at top of /gaming)</div>
              <input
                value={gamingConfig.hero_youtube_id || ""}
                onChange={(e) => setGamingConfig({ ...gamingConfig, hero_youtube_id: e.target.value })}
                placeholder="https://youtu.be/... or YouTube video ID"
                className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm"
                data-testid="admin-hero-video"
              />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-2">DISCORD INVITE LINK</div>
              <input
                value={gamingConfig.discord_invite_url || ""}
                onChange={(e) => setGamingConfig({ ...gamingConfig, discord_invite_url: e.target.value })}
                placeholder="https://discord.gg/..."
                className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono text-sm"
                data-testid="admin-discord-url"
              />
            </div>
            <button onClick={saveGamingConfig} className="font-mono text-xs px-4 py-2 bg-[#E63946] text-white" data-testid="admin-save-gaming-config">SAVE</button>
          </div>

          {/* Streamers */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl">STREAMERS</h3>
              <button onClick={newStreamer} className="font-mono text-xs px-3 py-2 bg-[#E63946] text-white inline-flex items-center gap-1" data-testid="admin-new-streamer">
                <Plus className="w-3 h-3" /> NEW
              </button>
            </div>
            {editingStreamer && (
              <div className="bg-[#151515] border border-[#E63946] p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-streamer-edit">
                <div>
                  <Label>DISPLAY NAME</Label>
                  <input value={streamerForm.name} onChange={(e) => setStreamerForm({ ...streamerForm, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>PLATFORM</Label>
                  <select value={streamerForm.platform} onChange={(e) => setStreamerForm({ ...streamerForm, platform: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2">
                    <option value="twitch">Twitch</option>
                    <option value="youtube">YouTube</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label>HANDLE / CHANNEL NAME (no URL)</Label>
                  <input value={streamerForm.handle} onChange={(e) => setStreamerForm({ ...streamerForm, handle: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="e.g. ninja" />
                </div>
                <div>
                  <Label>POSITION (first = featured live player)</Label>
                  <input type="number" value={streamerForm.position} onChange={(e) => setStreamerForm({ ...streamerForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <button onClick={saveStreamer} className="font-display text-base bg-[#E63946] text-white px-5 py-2" data-testid="admin-save-streamer">SAVE</button>
                  <button onClick={() => setEditingStreamer(null)} className="font-display text-base border border-white/30 px-5 py-2">CANCEL</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-streamers-list">
              {streamers.map((s) => (
                <div key={s.id} className="bg-[#151515] border border-white/10 p-3 flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate">{s.name}</div>
                    <div className="font-mono text-[10px] text-white/50">{s.platform.toUpperCase()} · @{s.handle} · #{s.position}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => startEditStreamer(s)} className="p-1.5 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => deleteStreamer(s.id)} className="p-1.5 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-streamer-${s.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {streamers.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No streamers yet.</div>}
            </div>
          </div>

          {/* Twitch VODs */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl">TWITCH VODS (past broadcasts)</h3>
              <button onClick={newVod} className="font-mono text-xs px-3 py-2 bg-[#9146FF] text-white inline-flex items-center gap-1" data-testid="admin-new-vod">
                <Plus className="w-3 h-3" /> NEW
              </button>
            </div>
            {editingVod && (
              <div className="bg-[#151515] border border-[#9146FF] p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-vod-edit">
                <div className="md:col-span-2">
                  <Label>TITLE (optional)</Label>
                  <input value={vodForm.title} onChange={(e) => setVodForm({ ...vodForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <Label>TWITCH VOD URL OR ID</Label>
                  <input value={vodForm.video_id} onChange={(e) => setVodForm({ ...vodForm, video_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="https://www.twitch.tv/videos/2008145632 or 2008145632" />
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={vodForm.position} onChange={(e) => setVodForm({ ...vodForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <button onClick={saveVod} className="font-display text-base bg-[#9146FF] text-white px-5 py-2" data-testid="admin-save-vod">SAVE</button>
                  <button onClick={() => setEditingVod(null)} className="font-display text-base border border-white/30 px-5 py-2">CANCEL</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-vods-list">
              {vods.map((v) => (
                <div key={v.id} className="bg-[#151515] border border-white/10 p-3 flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-base truncate">{v.title || `VOD ${v.video_id}`}</div>
                    <div className="font-mono text-[10px] text-white/40">#{v.position} · v{v.video_id}</div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => startEditVod(v)} className="p-1.5 border border-white/20 hover:border-[#9146FF]"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => deleteVod(v.id)} className="p-1.5 border border-white/20 hover:border-[#9146FF]" data-testid={`admin-delete-vod-${v.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {vods.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No Twitch VODs yet.</div>}
            </div>
          </div>

          {/* Clips */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl">GAMEPLAY CLIPS (YouTube)</h3>
              <button onClick={newClip} className="font-mono text-xs px-3 py-2 bg-[#E63946] text-white inline-flex items-center gap-1" data-testid="admin-new-clip">
                <Plus className="w-3 h-3" /> NEW
              </button>
            </div>
            {editingClip && (
              <div className="bg-[#151515] border border-[#E63946] p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-clip-edit">
                <div className="md:col-span-2">
                  <Label>TITLE</Label>
                  <input value={clipForm.title} onChange={(e) => setClipForm({ ...clipForm, title: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <Label>YOUTUBE URL / ID</Label>
                  <input value={clipForm.youtube_id} onChange={(e) => setClipForm({ ...clipForm, youtube_id: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="https://youtube.com/watch?v=..." />
                </div>
                <div>
                  <Label>GAME (used as section header)</Label>
                  <input value={clipForm.game} onChange={(e) => setClipForm({ ...clipForm, game: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="e.g. Fortnite, COD" />
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={clipForm.position} onChange={(e) => setClipForm({ ...clipForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <button onClick={saveClip} className="font-display text-base bg-[#E63946] text-white px-5 py-2" data-testid="admin-save-clip">SAVE</button>
                  <button onClick={() => setEditingClip(null)} className="font-display text-base border border-white/30 px-5 py-2">CANCEL</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-clips-list">
              {clips.map((c) => (
                <div key={c.id} className="bg-[#151515] border border-white/10 p-3">
                  <div className="font-mono text-[10px] text-[#FCA311] mb-1">{c.game?.toUpperCase() || "—"}</div>
                  <div className="font-display text-base truncate">{c.title || c.youtube_id}</div>
                  <div className="font-mono text-[10px] text-white/40 truncate">#{c.position} · {c.youtube_id}</div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => startEditClip(c)} className="p-1.5 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => deleteClip(c.id)} className="p-1.5 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-clip-${c.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {clips.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No clips yet.</div>}
            </div>
          </div>

          {/* Squad */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl">SQUAD ROSTER</h3>
              <button onClick={newSquadMember} className="font-mono text-xs px-3 py-2 bg-[#E63946] text-white inline-flex items-center gap-1" data-testid="admin-new-squad">
                <Plus className="w-3 h-3" /> NEW
              </button>
            </div>
            {editingSquadMember && (
              <div className="bg-[#151515] border border-[#E63946] p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-squad-edit">
                <div>
                  <Label>NAME</Label>
                  <input value={squadForm.name} onChange={(e) => setSquadForm({ ...squadForm, name: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>GAMERTAG</Label>
                  <input value={squadForm.gamertag} onChange={(e) => setSquadForm({ ...squadForm, gamertag: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>PRIMARY PLATFORM</Label>
                  <select value={squadForm.platform} onChange={(e) => setSquadForm({ ...squadForm, platform: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2">
                    <option value="pc">PC</option>
                    <option value="xbox">Xbox</option>
                    <option value="ps">PlayStation</option>
                    <option value="switch">Switch</option>
                    <option value="mobile">Mobile</option>
                  </select>
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input type="number" value={squadForm.position} onChange={(e) => setSquadForm({ ...squadForm, position: parseInt(e.target.value) || 0 })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <Label>FAVORITE GAMES (comma separated)</Label>
                  <input
                    value={squadForm.favorite_games.join(", ")}
                    onChange={(e) => setSquadForm({ ...squadForm, favorite_games: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2"
                    placeholder="Fortnite, COD, 2K"
                  />
                </div>
                <div>
                  <Label>TWITCH HANDLE</Label>
                  <input value={squadForm.twitch_handle || ""} onChange={(e) => setSquadForm({ ...squadForm, twitch_handle: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div>
                  <Label>YOUTUBE HANDLE</Label>
                  <input value={squadForm.youtube_handle || ""} onChange={(e) => setSquadForm({ ...squadForm, youtube_handle: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" />
                </div>
                <div className="md:col-span-2">
                  <Label>DISCORD HANDLE</Label>
                  <input value={squadForm.discord_handle || ""} onChange={(e) => setSquadForm({ ...squadForm, discord_handle: e.target.value })} className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2" placeholder="e.g. user#1234" />
                </div>
                <div className="md:col-span-2">
                  <Label>AVATAR</Label>
                  {squadForm.avatar_path && (
                    <div className="mb-2 w-20 h-20 border border-white/20">
                      <img src={imgUrl(squadForm.avatar_path)} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <label className="font-mono text-xs px-3 py-2 border border-white/20 inline-flex items-center gap-2 cursor-pointer hover:border-[#E63946]">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD AVATAR
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                      if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setSquadForm((cur) => ({ ...cur, avatar_path: d.storage_path })));
                    }} />
                  </label>
                </div>
                <div className="md:col-span-2 flex gap-2">
                  <button onClick={saveSquadMember} className="font-display text-base bg-[#E63946] text-white px-5 py-2" data-testid="admin-save-squad">SAVE</button>
                  <button onClick={() => setEditingSquadMember(null)} className="font-display text-base border border-white/30 px-5 py-2">CANCEL</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-squad-list">
              {squad.map((m) => (
                <div key={m.id} className="bg-[#151515] border border-white/10 p-3 flex gap-3">
                  <div className="w-14 h-14 bg-black border border-white/20 flex-shrink-0 overflow-hidden">
                    {m.avatar_path ? <img src={imgUrl(m.avatar_path)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/40 font-display text-xl">{m.name.slice(0,1).toUpperCase()}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">{m.name}</div>
                    <div className="font-mono text-[10px] text-white/50">{m.platform.toUpperCase()} · @{m.gamertag || "—"}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{(m.favorite_games || []).join(", ") || "—"}</div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => startEditSquad(m)} className="p-1.5 border border-white/20 hover:border-[#E63946]"><Pencil className="w-3 h-3" /></button>
                    <button onClick={() => deleteSquadMember(m.id)} className="p-1.5 border border-white/20 hover:border-[#E63946]" data-testid={`admin-delete-squad-${m.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {squad.length === 0 && <div className="font-mono text-xs text-white/40 col-span-full">No squad members yet.</div>}
            </div>
          </div>
        </div>
      )}

      {tab === "player" && playerDraft && (
        <div className="space-y-6" data-testid="admin-player">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-3xl">FAM RADIO PLAYER</h2>
              <p className="text-white/50 text-sm mt-1 max-w-2xl">
                Rebrand every label, chip and pill on the home-page player. All fields save to the <span className="font-mono text-white/70">player_config</span> singleton and apply instantly.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPlayerDraft(playerConfig)}
                disabled={JSON.stringify(playerDraft) === JSON.stringify(playerConfig) || playerSaving}
                className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/60 hover:text-white disabled:opacity-30"
                data-testid="player-reset"
              >
                RESET
              </button>
              <button
                onClick={async () => {
                  setPlayerSaving(true);
                  try {
                    const { key: _k, ...body } = playerDraft;
                    void _k;
                    const res = await api.put("/admin/player/config", body);
                    setPlayerConfig(res.data);
                    setPlayerDraft(res.data);
                    toast.success("Player saved");
                    // Broadcast so any mounted MediaPlayerProvider — this
                    // tab AND every other open tab — refetches the config
                    // instantly. FamRadio tabs, creator pill picker, and
                    // MiniPlayer stay in lock-step with admin edits.
                    try { window.dispatchEvent(new Event("syndafam:player-config-updated")); } catch (_) { /* noop */ }
                    try {
                      if (typeof BroadcastChannel !== "undefined") {
                        const bc = new BroadcastChannel("syndafam:player-config");
                        bc.postMessage({ type: "updated", at: Date.now() });
                        bc.close();
                      }
                    } catch (_) { /* noop */ }
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Save failed");
                  } finally {
                    setPlayerSaving(false);
                  }
                }}
                disabled={JSON.stringify(playerDraft) === JSON.stringify(playerConfig) || playerSaving}
                className="font-mono text-[11px] tracking-widest px-4 py-2 bg-[#E63946] text-white hover:bg-[#C92A36] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="player-save"
              >
                {playerSaving ? "SAVING…" : "SAVE ALL"}
              </button>
            </div>
          </div>

          {/* Header + copy */}
          <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <AboutField label="TITLE (main)" value={playerDraft.title} onChange={(v) => setPlayerDraft({ ...playerDraft, title: v })} testid="player-field-title" />
            <AboutField label="TITLE ACCENT (period / dot)" value={playerDraft.title_accent} onChange={(v) => setPlayerDraft({ ...playerDraft, title_accent: v })} testid="player-field-accent" />
            <AboutField label="SUBSCRIBE BUTTON LABEL" value={playerDraft.subscribe_label} onChange={(v) => setPlayerDraft({ ...playerDraft, subscribe_label: v })} testid="player-field-sub-label" />
            <AboutField label="SUBSCRIBE URL" value={playerDraft.subscribe_url} onChange={(v) => setPlayerDraft({ ...playerDraft, subscribe_url: v })} testid="player-field-sub-url" />
            <AboutField label="SEARCH PLACEHOLDER" value={playerDraft.search_placeholder} onChange={(v) => setPlayerDraft({ ...playerDraft, search_placeholder: v })} testid="player-field-search-ph" />
            <AboutField label="EMPTY MESSAGE" value={playerDraft.empty_message} onChange={(v) => setPlayerDraft({ ...playerDraft, empty_message: v })} testid="player-field-empty" />
          </div>

          {/* Media types */}
          <div className="bg-[#151515] border border-white/10 p-6 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-white/40">MEDIA TYPE PILLS (key is any lowercase slug you like — e.g. video, audio, instrumental, podcast, live-sets — creators will see these as tag options)</div>
            {(playerDraft.media_types || []).map((mt, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[130px_1fr_140px_auto] gap-3 items-center border border-white/5 p-3" data-testid={`player-mt-${i}`}>
                <input value={mt.key || ""} onChange={(e) => { const arr = [...playerDraft.media_types]; arr[i] = { ...arr[i], key: e.target.value }; setPlayerDraft({ ...playerDraft, media_types: arr }); }} placeholder="key" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                <input value={mt.label || ""} onChange={(e) => { const arr = [...playerDraft.media_types]; arr[i] = { ...arr[i], label: e.target.value }; setPlayerDraft({ ...playerDraft, media_types: arr }); }} placeholder="LABEL" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest" />
                <div className="flex items-center gap-2">
                  <input type="color" value={mt.color || "#67E8F9"} onChange={(e) => { const arr = [...playerDraft.media_types]; arr[i] = { ...arr[i], color: e.target.value }; setPlayerDraft({ ...playerDraft, media_types: arr }); }} className="w-10 h-8 bg-black border border-white/10 cursor-pointer" />
                  <input value={mt.color || ""} onChange={(e) => { const arr = [...playerDraft.media_types]; arr[i] = { ...arr[i], color: e.target.value }; setPlayerDraft({ ...playerDraft, media_types: arr }); }} placeholder="#RRGGBB" className="flex-1 bg-black border border-white/10 outline-none px-2 py-2 font-mono text-xs" />
                </div>
                <button onClick={() => { const arr = [...playerDraft.media_types]; arr.splice(i, 1); setPlayerDraft({ ...playerDraft, media_types: arr }); }} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => {
              const cur = playerDraft.media_types || [];
              let n = cur.length + 1;
              const existing = new Set(cur.map((m) => m.key));
              let key = `section-${n}`;
              while (existing.has(key)) { n++; key = `section-${n}`; }
              setPlayerDraft({ ...playerDraft, media_types: [...cur, { key, label: "NEW", color: "#67E8F9" }] });
            }} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD MEDIA TYPE</button>
          </div>

          {/* TRACKS PER MEDIA TYPE — quick add + inline management */}
          <div className="space-y-4" data-testid="player-tracks-by-type">
            <div className="font-mono text-[10px] tracking-widest text-white/40">
              // TRACKS PER MEDIA TYPE — drag any row by its grip handle to reorder, tap the pencil to edit full details, or use the add form to append a new track. Order changes apply instantly to the live player.
            </div>
            {(playerDraft.media_types || []).map((mt) => {
              const key = mt.key || "audio";
              const color = mt.color || "#FCA311";
              const label = mt.label || key.toUpperCase();
              const typeTracks = tracks.filter((t) => {
                const types = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
                return types.includes(key);
              }).sort((a, b) => (a.position || 0) - (b.position || 0));
              const form = quickAddByType[key] || { title: "", artist: "", youtube_id: "", audio_url: "" };
              const setForm = (patch) => setQuickAddByType({ ...quickAddByType, [key]: { ...form, ...patch } });
              const canSave = form.title && (form.youtube_id || form.audio_url);
              return (
                <div key={key} className="bg-[#151515] border-2 p-5 space-y-3" style={{ borderColor: `${color}55` }} data-testid={`player-tracks-block-${key}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[10px] tracking-[0.3em] px-3 py-1 border-2" style={{ color, borderColor: color }}>{label}</span>
                      <span className="font-mono text-[10px] text-white/50">{typeTracks.length} track{typeTracks.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>

                  {/* Existing tracks — compact draggable list */}
                  {typeTracks.length > 0 && (
                    <div className="border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid={`player-tracks-list-${key}`}>
                      {typeTracks.map((t, ti) => {
                        const isDragging = dragTrackId === t.id;
                        const isDropTarget = dragTypeKey === key && dragOverId === t.id && dragTrackId && dragTrackId !== t.id;
                        return (
                          <div
                            key={t.id}
                            draggable
                            onDragStart={(e) => {
                              setDragTypeKey(key);
                              setDragTrackId(t.id);
                              try {
                                e.dataTransfer.effectAllowed = "move";
                                e.dataTransfer.setData("text/plain", t.id);
                              } catch { /* dt might be locked */ }
                            }}
                            onDragEnd={() => {
                              setDragTypeKey(null);
                              setDragTrackId(null);
                              setDragOverId(null);
                              setDragOverPos("before");
                            }}
                            onDragOver={(e) => {
                              // Only accept drops from the same media-type block.
                              if (dragTypeKey !== key || !dragTrackId || dragTrackId === t.id) return;
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                              // Decide whether we're in the top or bottom half of this row.
                              const rect = e.currentTarget.getBoundingClientRect();
                              const half = rect.top + rect.height / 2;
                              const pos = e.clientY < half ? "before" : "after";
                              if (dragOverId !== t.id || dragOverPos !== pos) {
                                setDragOverId(t.id);
                                setDragOverPos(pos);
                              }
                            }}
                            onDragLeave={(e) => {
                              // Only clear when we actually leave the row (not entering a child).
                              if (!e.currentTarget.contains(e.relatedTarget)) {
                                if (dragOverId === t.id) setDragOverId(null);
                              }
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              if (dragTypeKey === key && dragTrackId && dragTrackId !== t.id) {
                                reorderTrackInType(dragTrackId, t.id, key, dragOverPos === "after");
                              }
                              setDragTypeKey(null);
                              setDragTrackId(null);
                              setDragOverId(null);
                              setDragOverPos("before");
                            }}
                            className={`relative grid grid-cols-[16px_24px_1fr_auto] gap-2 items-center px-3 py-2 transition-opacity ${isDragging ? "opacity-40" : "opacity-100"} cursor-grab active:cursor-grabbing`}
                            data-testid={`player-track-row-${key}-${t.id}`}
                          >
                            {/* Drop indicator lines above/below */}
                            {isDropTarget && dragOverPos === "before" && (
                              <span className="pointer-events-none absolute -top-[1px] left-0 right-0 h-[2px] bg-[#FCA311] shadow-[0_0_8px_rgba(252,163,17,0.9)]" />
                            )}
                            {isDropTarget && dragOverPos === "after" && (
                              <span className="pointer-events-none absolute -bottom-[1px] left-0 right-0 h-[2px] bg-[#FCA311] shadow-[0_0_8px_rgba(252,163,17,0.9)]" />
                            )}
                            <span className="text-white/25 hover:text-white/70" title="Drag to reorder" data-testid={`player-track-handle-${key}-${t.id}`}>
                              <GripVertical className="w-4 h-4" />
                            </span>
                            <div className="font-mono text-[10px] text-white/30 tabular-nums text-right">{String(ti + 1).padStart(2, "0")}</div>
                            <div className="min-w-0">
                              <div className="font-display text-sm truncate">{t.title || "(untitled)"}</div>
                              <div className="font-mono text-[10px] text-white/40 truncate">
                                {t.artist || "—"} · {t.youtube_id ? `YT:${t.youtube_id}` : (t.audio_url ? "audio file" : "no source")}
                              </div>
                            </div>
                            <div className="flex items-center gap-0.5" onDragStart={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => { startEditTrack(t); setTab("playlist"); setTimeout(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, 50); }}
                                className="p-1.5 text-white/40 hover:text-[#67E8F9]"
                                title="Edit full track details"
                                data-testid={`player-track-edit-${t.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={async () => {
                                  const curTypes = Array.isArray(t.media_types) && t.media_types.length ? t.media_types : [t.media_type || "audio"];
                                  const remaining = curTypes.filter((k) => k !== key);
                                  if (remaining.length === 0) {
                                    if (!window.confirm(`"${t.title}" is only in ${label}. Remove will DELETE the track entirely — continue?`)) return;
                                    try {
                                      await api.delete(`/admin/playlist/${t.id}`);
                                      const r = await api.get("/playlist");
                                      setTracks(r.data || []);
                                      toast.success("Track deleted");
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Delete failed");
                                    }
                                    return;
                                  }
                                  if (!window.confirm(`Remove "${t.title}" from ${label}? (Track will still appear in: ${remaining.join(", ")})`)) return;
                                  try {
                                    await api.put(`/admin/playlist/${t.id}`, {
                                      youtube_id: t.youtube_id || "",
                                      audio_url: t.audio_url || "",
                                      title: t.title || "",
                                      artist: t.artist || "",
                                      position: t.position || 0,
                                      active: t.active !== false,
                                      media_type: remaining[0],
                                      media_types: remaining,
                                      genre: t.genre || "",
                                      profession: t.profession || "",
                                      artwork_url: t.artwork_url || "",
                                    });
                                    const r = await api.get("/playlist");
                                    setTracks(r.data || []);
                                    toast.success(`Removed from ${label}`);
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Update failed");
                                  }
                                }}
                                className="p-1.5 text-white/40 hover:text-[#E63946]"
                                title={`Remove from ${label}`}
                                data-testid={`player-track-remove-${t.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Quick add form */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-white/10">
                    <input
                      value={form.title}
                      onChange={(e) => setForm({ title: e.target.value })}
                      placeholder="Track title"
                      className="bg-black border border-white/15 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs"
                      data-testid={`player-add-title-${key}`}
                    />
                    <input
                      value={form.artist}
                      onChange={(e) => setForm({ artist: e.target.value })}
                      placeholder="Artist(s) — comma / feat. / &"
                      className="bg-black border border-white/15 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs"
                      data-testid={`player-add-artist-${key}`}
                    />
                    <input
                      value={form.youtube_id}
                      onChange={(e) => setForm({ youtube_id: e.target.value, audio_url: "" })}
                      placeholder="YouTube URL or ID (leave blank for file upload)"
                      className="bg-black border border-white/15 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs md:col-span-2"
                      data-testid={`player-add-yt-${key}`}
                    />
                    <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                      <input
                        value={form.audio_url}
                        onChange={(e) => setForm({ audio_url: e.target.value, youtube_id: "" })}
                        placeholder="…or audio URL (mp3/m4a)"
                        className="flex-1 bg-black border border-white/15 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs min-w-[240px]"
                        data-testid={`player-add-url-${key}`}
                      />
                      <label className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/70 hover:border-[#FCA311] hover:text-[#FCA311] cursor-pointer inline-flex items-center gap-1.5">
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} UPLOAD
                        <input
                          type="file"
                          accept="audio/*,video/*"
                          className="hidden"
                          data-testid={`player-add-upload-${key}`}
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              uploadFile(e.target.files[0], key === "video" ? "video" : "audio", (d) => setForm({
                                audio_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${d.storage_path}`,
                                youtube_id: "",
                              }));
                              e.target.value = "";
                            }
                          }}
                        />
                      </label>
                      <button
                        onClick={async () => {
                          if (!canSave) return toast.error("Need title + a YouTube URL or audio file");
                          setQuickAdding(key);
                          try {
                            await api.post("/admin/playlist", {
                              title: form.title,
                              artist: form.artist,
                              youtube_id: form.youtube_id || "",
                              audio_url: form.audio_url || "",
                              media_type: key,
                              position: typeTracks.length + 1,
                              active: true,
                              genre: "",
                              profession: "",
                              artwork_url: "",
                            });
                            const r = await api.get("/playlist");
                            setTracks(r.data || []);
                            setQuickAddByType({ ...quickAddByType, [key]: { title: "", artist: "", youtube_id: "", audio_url: "" } });
                            toast.success(`Added to ${label}`);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Add failed");
                          } finally {
                            setQuickAdding(null);
                          }
                        }}
                        disabled={!canSave || quickAdding === key}
                        className="font-mono text-[11px] tracking-widest px-4 py-2 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: canSave ? color : "#333", color: canSave ? "#0A0A0A" : "#666" }}
                        data-testid={`player-add-save-${key}`}
                      >
                        {quickAdding === key ? "ADDING…" : `+ ADD TO ${label}`}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Sections */}
          <div className="bg-[#151515] border border-white/10 p-6 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-white/40">SECTION TABS (must include: artists, genres, profession — used by internal filter logic)</div>
            {(playerDraft.sections || []).map((s, i) => (
              <div key={i} className="grid grid-cols-[150px_1fr_auto] gap-3 items-center border border-white/5 p-3" data-testid={`player-sec-${i}`}>
                <input value={s.key || ""} onChange={(e) => { const arr = [...playerDraft.sections]; arr[i] = { ...arr[i], key: e.target.value }; setPlayerDraft({ ...playerDraft, sections: arr }); }} className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                <input value={s.label || ""} onChange={(e) => { const arr = [...playerDraft.sections]; arr[i] = { ...arr[i], label: e.target.value }; setPlayerDraft({ ...playerDraft, sections: arr }); }} className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest" />
                <button onClick={() => { const arr = [...playerDraft.sections]; arr.splice(i, 1); setPlayerDraft({ ...playerDraft, sections: arr }); }} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          {/* Professions */}
          <div className="bg-[#151515] border border-white/10 p-6 space-y-3">
            <div className="font-mono text-[10px] tracking-widest text-white/40">PROFESSION CHIPS (each links to a URL)</div>
            {(playerDraft.professions || []).map((pr, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[150px_1fr_1fr_auto] gap-3 items-center border border-white/5 p-3" data-testid={`player-pr-${i}`}>
                <input value={pr.key || ""} onChange={(e) => { const arr = [...playerDraft.professions]; arr[i] = { ...arr[i], key: e.target.value }; setPlayerDraft({ ...playerDraft, professions: arr }); }} placeholder="slug" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                <input value={pr.label || ""} onChange={(e) => { const arr = [...playerDraft.professions]; arr[i] = { ...arr[i], label: e.target.value }; setPlayerDraft({ ...playerDraft, professions: arr }); }} placeholder="LABEL" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest" />
                <input value={pr.url || ""} onChange={(e) => { const arr = [...playerDraft.professions]; arr[i] = { ...arr[i], url: e.target.value }; setPlayerDraft({ ...playerDraft, professions: arr }); }} placeholder="/connect?profession=..." className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                <button onClick={() => { const arr = [...playerDraft.professions]; arr.splice(i, 1); setPlayerDraft({ ...playerDraft, professions: arr }); }} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={() => setPlayerDraft({ ...playerDraft, professions: [...(playerDraft.professions || []), { key: "new", label: "NEW", url: "/connect?profession=new" }] })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD PROFESSION</button>
          </div>
        </div>
      )}

      {tab === "about" && (
        <div className="space-y-6" data-testid="admin-about">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-3xl">ABOUT PAGE</h2>
              <p className="text-white/50 text-sm mt-1 max-w-2xl">
                Full customization of the <span className="font-mono text-white/70">/about</span> page. Edit any section — text, arrays, images. Image fields accept either a URL, an absolute path like <span className="font-mono text-white/60">/about/team-shao.jpg</span>, or a filename returned by the Upload button.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setAboutDraft(aboutConfig)}
                disabled={!aboutConfig || JSON.stringify(aboutDraft) === JSON.stringify(aboutConfig) || aboutSaving}
                className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/60 hover:text-white disabled:opacity-30"
                data-testid="about-reset"
              >
                RESET
              </button>
              <button
                onClick={async () => {
                  if (!aboutDraft) return;
                  setAboutSaving(true);
                  try {
                    const { key: _k, ...body } = aboutDraft;
                    void _k;
                    const res = await api.put("/admin/about/config", body);
                    setAboutConfig(res.data);
                    setAboutDraft(res.data);
                    toast.success("About page saved");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Save failed");
                  } finally {
                    setAboutSaving(false);
                  }
                }}
                disabled={!aboutDraft || JSON.stringify(aboutDraft) === JSON.stringify(aboutConfig) || aboutSaving}
                className="font-mono text-[11px] tracking-widest px-4 py-2 bg-[#E63946] text-white hover:bg-[#C92A36] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="about-save"
              >
                {aboutSaving ? "SAVING…" : "SAVE ALL"}
              </button>
            </div>
          </div>

          {!aboutDraft && <div className="font-mono text-xs text-white/40">Loading about config…</div>}

          {aboutDraft && (
            <>
              {/* Subsection tabs */}
              <div className="flex flex-wrap border border-white/10 overflow-x-auto">
                {["hero", "stats", "origin", "history", "vision", "mission", "services", "message", "team", "contact"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setAboutSection(s)}
                    className={`font-mono text-[11px] tracking-widest px-3 py-2 border-r border-white/10 last:border-r-0 transition-colors ${aboutSection === s ? "bg-[#FCA311] text-black" : "text-white/50 hover:text-white"}`}
                    data-testid={`about-subtab-${s}`}
                  >
                    {s.toUpperCase()}
                  </button>
                ))}
              </div>

              {(() => {
                const set = (k, v) => setAboutDraft({ ...aboutDraft, [k]: v });
                const setItem = (k, idx, patch) => {
                  const list = [...(aboutDraft[k] || [])];
                  list[idx] = { ...list[idx], ...patch };
                  set(k, list);
                };
                const addItem = (k, blank) => set(k, [...(aboutDraft[k] || []), blank]);
                const removeItem = (k, idx) => {
                  const list = [...(aboutDraft[k] || [])];
                  list.splice(idx, 1);
                  set(k, list);
                };
                const field = (props) => (
                  <AboutField
                    label={props.label}
                    textarea={props.textarea}
                    rows={props.rows}
                    placeholder={props.placeholder}
                    testid={`about-field-${props.k}`}
                    value={aboutDraft[props.k]}
                    onChange={(v) => set(props.k, v)}
                  />
                );
                const imageField = (props) => (
                  <AboutImageField
                    label={props.label}
                    testid={`about-field-${props.k}`}
                    value={aboutDraft[props.k]}
                    onChange={(v) => set(props.k, v)}
                    onUpload={(file) => uploadFile(file, "image", (d) => set(props.k, d.storage_path))}
                  />
                );

                switch (aboutSection) {
                  case "hero":
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-[#151515] border border-white/10 p-6">
                        {field({ label: "EYEBROW (small red label)", k: "hero_eyebrow" })}
                        {field({ label: "SUBTITLE", k: "hero_subtitle", textarea: true, rows: 3 })}
                        {field({ label: "TITLE LINE 1 (white)", k: "hero_line_1" })}
                        {field({ label: "TITLE LINE 2 (red)", k: "hero_line_2" })}
                        {field({ label: "TITLE LINE 3 (graffiti, yellow, lowercase)", k: "hero_line_3" })}
                        {imageField({ label: "HERO BACKGROUND IMAGE", k: "hero_bg_image" })}
                        {imageField({ label: "HERO COVER (side card)", k: "hero_cover_image" })}
                      </div>
                    );
                  case "stats":
                    return (
                      <div className="bg-[#151515] border border-white/10 p-6 space-y-3">
                        <div className="font-mono text-[10px] tracking-widest text-white/40">STATS STRIP (4 items recommended)</div>
                        {(aboutDraft.stats || []).map((s, i) => (
                          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid={`about-stat-${i}`}>
                            <div>
                              <label className="font-mono text-[9px] tracking-widest text-white/40">VALUE</label>
                              <input value={s.value || ""} onChange={(e) => setItem("stats", i, { value: e.target.value })} className="w-full mt-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-display text-lg" />
                            </div>
                            <div>
                              <label className="font-mono text-[9px] tracking-widest text-white/40">LABEL</label>
                              <input value={s.label || ""} onChange={(e) => setItem("stats", i, { label: e.target.value })} className="w-full mt-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest" />
                            </div>
                            <button onClick={() => removeItem("stats", i)} className="p-2 text-white/40 hover:text-[#E63946]" title="Remove"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        ))}
                        <button onClick={() => addItem("stats", { value: "", label: "" })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD STAT</button>
                      </div>
                    );
                  case "origin":
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-[#151515] border border-white/10 p-6">
                        {field({ label: "EYEBROW", k: "origin_eyebrow" })}
                        <div />
                        {field({ label: "TITLE LINE 1", k: "origin_title_1" })}
                        {field({ label: "TITLE LINE 2 (red)", k: "origin_title_2" })}
                        <div className="md:col-span-2 space-y-3">
                          <label className="font-mono text-[10px] tracking-widest text-white/50">PARAGRAPHS</label>
                          {(aboutDraft.origin_paragraphs || []).map((p, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <textarea value={p} onChange={(e) => { const arr = [...(aboutDraft.origin_paragraphs || [])]; arr[i] = e.target.value; set("origin_paragraphs", arr); }} rows={3} className="flex-1 bg-black border border-white/10 focus:border-[#E63946] outline-none p-3 font-mono text-sm leading-relaxed resize-y" data-testid={`about-origin-para-${i}`} />
                              <button onClick={() => { const arr = [...(aboutDraft.origin_paragraphs || [])]; arr.splice(i,1); set("origin_paragraphs", arr); }} className="p-2 text-white/40 hover:text-[#E63946] mt-1"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button onClick={() => set("origin_paragraphs", [...(aboutDraft.origin_paragraphs || []), ""])} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD PARAGRAPH</button>
                        </div>
                        {imageField({ label: "ORIGIN PHOTO", k: "origin_photo" })}
                        <div className="space-y-3">
                          {field({ label: "PHOTO CAPTION TAG", k: "origin_photo_tag" })}
                          {field({ label: "PHOTO CAPTION", k: "origin_photo_caption" })}
                        </div>
                      </div>
                    );
                  case "history":
                    return (
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 bg-[#151515] border border-white/10 p-6">
                          {field({ label: "EYEBROW", k: "history_eyebrow" })}
                          <div />
                          {field({ label: "TITLE LINE 1", k: "history_title_1" })}
                          {field({ label: "TITLE LINE 2 (graffiti yellow subtitle)", k: "history_title_2" })}
                          <div className="md:col-span-2 space-y-3">
                            <label className="font-mono text-[10px] tracking-widest text-white/50">HISTORY PARAGRAPHS</label>
                            {(aboutDraft.history_paragraphs || []).map((p, i) => (
                              <div key={i} className="flex items-start gap-3">
                                <textarea value={p} onChange={(e) => { const arr = [...(aboutDraft.history_paragraphs || [])]; arr[i] = e.target.value; set("history_paragraphs", arr); }} rows={3} className="flex-1 bg-black border border-white/10 focus:border-[#E63946] outline-none p-3 font-mono text-sm leading-relaxed resize-y" />
                                <button onClick={() => { const arr = [...(aboutDraft.history_paragraphs || [])]; arr.splice(i,1); set("history_paragraphs", arr); }} className="p-2 text-white/40 hover:text-[#E63946] mt-1"><Trash2 className="w-4 h-4" /></button>
                              </div>
                            ))}
                            <button onClick={() => set("history_paragraphs", [...(aboutDraft.history_paragraphs || []), ""])} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD PARAGRAPH</button>
                          </div>
                          {imageField({ label: "HISTORY PHOTO", k: "history_photo" })}
                          <div className="space-y-3">
                            {field({ label: "PHOTO CAPTION TAG", k: "history_photo_tag" })}
                            {field({ label: "PHOTO CAPTION", k: "history_photo_caption" })}
                          </div>
                        </div>

                        <div className="bg-[#151515] border border-white/10 p-6 space-y-4">
                          <div className="font-mono text-[10px] tracking-widest text-white/40">TIMELINE ENTRIES</div>
                          {(aboutDraft.timeline || []).map((t, i) => (
                            <div key={i} className="grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-3 items-start border border-white/5 p-3" data-testid={`about-timeline-${i}`}>
                              <input value={t.year || ""} onChange={(e) => setItem("timeline", i, { year: e.target.value })} placeholder="Year" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest text-[#FCA311]" />
                              <div className="space-y-2">
                                <input value={t.title || ""} onChange={(e) => setItem("timeline", i, { title: e.target.value })} placeholder="Title" className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-display text-lg" />
                                <textarea value={t.body || ""} onChange={(e) => setItem("timeline", i, { body: e.target.value })} rows={2} placeholder="Body" className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none p-2 font-mono text-xs leading-relaxed resize-y" />
                              </div>
                              <button onClick={() => removeItem("timeline", i)} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button onClick={() => addItem("timeline", { year: "", title: "", body: "" })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD TIMELINE ENTRY</button>
                        </div>
                      </div>
                    );
                  case "vision":
                    return (
                      <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {field({ label: "EYEBROW", k: "vision_eyebrow" })}
                        <div />
                        {field({ label: "TITLE LINE 1", k: "vision_title_1" })}
                        {field({ label: "TITLE LINE 2 (red)", k: "vision_title_2" })}
                        {field({ label: "BODY", k: "vision_body", textarea: true, rows: 5 })}
                        {imageField({ label: "BACKGROUND IMAGE", k: "vision_bg_image" })}
                        <div className="md:col-span-2 space-y-3">
                          <label className="font-mono text-[10px] tracking-widest text-white/50">PILLARS</label>
                          <div className="flex flex-wrap gap-2">
                            {(aboutDraft.vision_pillars || []).map((p, i) => (
                              <div key={i} className="flex items-center gap-2 bg-black border border-white/10 pl-3 pr-2 py-1">
                                <input value={p} onChange={(e) => { const arr = [...(aboutDraft.vision_pillars || [])]; arr[i] = e.target.value; set("vision_pillars", arr); }} className="bg-transparent outline-none font-mono text-xs tracking-widest w-32" />
                                <button onClick={() => { const arr = [...(aboutDraft.vision_pillars || [])]; arr.splice(i,1); set("vision_pillars", arr); }} className="text-white/40 hover:text-[#E63946]"><Trash2 className="w-3 h-3" /></button>
                              </div>
                            ))}
                            <button onClick={() => set("vision_pillars", [...(aboutDraft.vision_pillars || []), "NEW PILLAR"])} className="font-mono text-[10px] tracking-widest px-3 py-1 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-1"><Plus className="w-3 h-3" /> ADD</button>
                          </div>
                        </div>
                      </div>
                    );
                  case "mission":
                    return (
                      <div className="space-y-6">
                        <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                          {field({ label: "EYEBROW", k: "mission_eyebrow" })}
                          <div />
                          {field({ label: "TITLE LINE 1", k: "mission_title_1" })}
                          {field({ label: "TITLE LINE 2 (graffiti yellow)", k: "mission_title_2" })}
                        </div>
                        <div className="bg-[#151515] border border-white/10 p-6 space-y-4">
                          <div className="font-mono text-[10px] tracking-widest text-white/40">MISSION CARDS</div>
                          {(aboutDraft.missions || []).map((m, i) => (
                            <div key={i} className="border border-white/5 p-3 space-y-2" data-testid={`about-mission-${i}`}>
                              <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_auto] gap-3 items-start">
                                <input value={m.tag || ""} onChange={(e) => setItem("missions", i, { tag: e.target.value })} placeholder="Tag" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs tracking-widest text-[#FCA311]" />
                                <input value={m.title || ""} onChange={(e) => setItem("missions", i, { title: e.target.value })} placeholder="Title" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-display text-xl" />
                                <button onClick={() => removeItem("missions", i)} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                              </div>
                              <textarea value={m.body || ""} onChange={(e) => setItem("missions", i, { body: e.target.value })} rows={3} placeholder="Body" className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none p-3 font-mono text-sm leading-relaxed resize-y" />
                            </div>
                          ))}
                          <button onClick={() => addItem("missions", { tag: "MISSION 00X", title: "", body: "" })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD MISSION</button>
                        </div>
                      </div>
                    );
                  case "services":
                    return (
                      <div className="space-y-6">
                        <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                          {field({ label: "EYEBROW", k: "services_eyebrow" })}
                          <div />
                          {field({ label: "TITLE LINE 1", k: "services_title_1" })}
                          {field({ label: "TITLE LINE 2 (red)", k: "services_title_2" })}
                        </div>
                        <div className="bg-[#151515] border border-white/10 p-6 space-y-4">
                          <div className="font-mono text-[10px] tracking-widest text-white/40">SERVICE CARDS · icons: music, pen, palette, camera, globe, shirt, mic, sparkles</div>
                          {(aboutDraft.services || []).map((s, i) => (
                            <div key={i} className="border border-white/5 p-3 grid grid-cols-1 md:grid-cols-[110px_1fr_1fr_auto] gap-3 items-start" data-testid={`about-service-${i}`}>
                              <select value={s.icon || "music"} onChange={(e) => setItem("services", i, { icon: e.target.value })} className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-2 py-2 font-mono text-xs">
                                {Object.keys(ICON_MAP_ADMIN).map((k) => <option key={k} value={k}>{k}</option>)}
                              </select>
                              <div className="space-y-2">
                                <input value={s.title || ""} onChange={(e) => setItem("services", i, { title: e.target.value })} placeholder="Title" className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-display text-lg" />
                                <textarea value={s.body || ""} onChange={(e) => setItem("services", i, { body: e.target.value })} rows={3} placeholder="Body" className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none p-3 font-mono text-sm leading-relaxed resize-y" />
                              </div>
                              <div>
                                <div className="flex gap-2">
                                  <input value={s.image || ""} onChange={(e) => setItem("services", i, { image: e.target.value })} placeholder="/about/x.jpg" className="flex-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                                  <label className="cursor-pointer font-mono text-[10px] tracking-widest px-2 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311]">
                                    UPLOAD
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setItem("services", i, { image: d.storage_path })); }} />
                                  </label>
                                </div>
                                {s.image && <img src={imgUrl(s.image)} alt="" className="mt-2 max-h-20 border border-white/10 object-cover" />}
                              </div>
                              <button onClick={() => removeItem("services", i)} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button onClick={() => addItem("services", { icon: "music", title: "", body: "", image: "" })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD SERVICE</button>
                        </div>
                      </div>
                    );
                  case "message":
                    return (
                      <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 gap-5">
                        {field({ label: "EYEBROW", k: "message_eyebrow" })}
                        {field({ label: "MESSAGE BODY (use blank lines for paragraph breaks)", k: "message_body", textarea: true, rows: 10 })}
                        {field({ label: "SIGNATURE", k: "message_signature" })}
                      </div>
                    );
                  case "team":
                    return (
                      <div className="space-y-6">
                        <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                          {field({ label: "EYEBROW", k: "team_eyebrow" })}
                          {field({ label: "TITLE", k: "team_title" })}
                          {field({ label: "SUBTITLE", k: "team_subtitle", textarea: true, rows: 2 })}
                          {field({ label: "CTA BUTTON LABEL", k: "team_cta_label" })}
                        </div>
                        <div className="bg-[#151515] border border-white/10 p-6 space-y-4">
                          <div className="font-mono text-[10px] tracking-widest text-white/40">TEAM MEMBERS</div>
                          {(aboutDraft.team || []).map((m, i) => (
                            <div key={i} className="border border-white/5 p-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-start" data-testid={`about-team-${i}`}>
                              <input value={m.name || ""} onChange={(e) => setItem("team", i, { name: e.target.value })} placeholder="Name" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-display text-lg" />
                              <input value={m.role || ""} onChange={(e) => setItem("team", i, { role: e.target.value })} placeholder="Role · Role · Role" className="bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                              <div>
                                <div className="flex gap-2">
                                  <input value={m.photo || ""} onChange={(e) => setItem("team", i, { photo: e.target.value })} placeholder="/about/x.jpg" className="flex-1 bg-black border border-white/10 focus:border-[#E63946] outline-none px-3 py-2 font-mono text-xs" />
                                  <label className="cursor-pointer font-mono text-[10px] tracking-widest px-2 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311]">
                                    UPLOAD
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) uploadFile(e.target.files[0], "image", (d) => setItem("team", i, { photo: d.storage_path })); }} />
                                  </label>
                                </div>
                                {m.photo && <img src={imgUrl(m.photo)} alt="" className="mt-2 max-h-20 border border-white/10 object-cover" />}
                              </div>
                              <button onClick={() => removeItem("team", i)} className="p-2 text-white/40 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          ))}
                          <button onClick={() => addItem("team", { name: "", role: "", photo: "" })} className="font-mono text-[10px] tracking-widest px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311] flex items-center gap-2"><Plus className="w-3 h-3" /> ADD MEMBER</button>
                        </div>
                      </div>
                    );
                  case "contact":
                    return (
                      <div className="bg-[#151515] border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        {field({ label: "EYEBROW", k: "contact_eyebrow" })}
                        {field({ label: "TITLE", k: "contact_title" })}
                        {field({ label: "BODY", k: "contact_body", textarea: true, rows: 3 })}
                        <div />
                        {field({ label: "WEBSITE", k: "contact_website" })}
                        {field({ label: "EMAIL", k: "contact_email" })}
                        {field({ label: "ADDRESS", k: "contact_address" })}
                        {imageField({ label: "BACKGROUND IMAGE", k: "contact_bg_image" })}
                      </div>
                    );
                  default:
                    return null;
                }
              })()}
            </>
          )}
        </div>
      )}

      {tab === "traffic" && (
        <TrafficPanel
          range={trafficRange}
          setRange={setTrafficRange}
          data={trafficData}
          setData={setTrafficData}
          loading={trafficLoading}
          setLoading={setTrafficLoading}
        />
      )}


      {tab === "games" && (
        <div className="space-y-6" data-testid="admin-games">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display text-3xl">GAMES</h2>
              <p className="text-white/50 text-sm mt-1 max-w-2xl">
                Edit the raw HTML for each game, or upload a self-contained <span className="font-mono text-white/70">.html</span> file to replace it. Changes go live instantly at <span className="font-mono text-white/70">/api/games/&lt;slug&gt;/play</span> — no redeploy needed. Include all CSS/JS inline (max 5 MB).
              </p>
            </div>
            <button
              onClick={() => setShowNewGame((v) => !v)}
              className="font-mono text-xs px-4 py-2 bg-[#E63946] text-white inline-flex items-center gap-2 hover:bg-[#C92A36]"
              data-testid="admin-new-game-toggle"
            >
              <Plus className="w-3 h-3" /> {showNewGame ? "CANCEL" : "NEW GAME"}
            </button>
          </div>

          {showNewGame && (
            <div className="bg-[#151515] border border-[#E63946] p-5 space-y-3" data-testid="admin-new-game-form">
              <div className="font-mono text-[10px] tracking-widest text-[#FCA311]">// CREATE NEW GAME</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>SLUG (url-safe, e.g. flappy-synda)</Label>
                  <input
                    value={newGameForm.slug}
                    onChange={(e) => setNewGameForm({ ...newGameForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })}
                    placeholder="my-new-game"
                    className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2 font-mono"
                    data-testid="admin-new-game-slug"
                  />
                </div>
                <div>
                  <Label>TITLE</Label>
                  <input
                    value={newGameForm.title}
                    onChange={(e) => setNewGameForm({ ...newGameForm, title: e.target.value })}
                    placeholder="My New Game"
                    className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2"
                    data-testid="admin-new-game-title"
                  />
                </div>
                <div>
                  <Label>POSITION</Label>
                  <input
                    type="number"
                    value={newGameForm.position}
                    onChange={(e) => setNewGameForm({ ...newGameForm, position: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#0A0A0A] border border-white/20 px-3 py-2"
                    data-testid="admin-new-game-position"
                  />
                </div>
              </div>
              <div>
                <Label>HTML (upload a .html file or paste below — must be self-contained)</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  <label
                    className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/70 hover:border-[#FCA311] hover:text-[#FCA311] cursor-pointer inline-flex items-center gap-1.5"
                    data-testid="admin-new-game-upload-label"
                  >
                    <Upload className="w-3 h-3" /> UPLOAD .HTML
                    <input
                      type="file"
                      accept=".html,.htm,text/html"
                      className="hidden"
                      data-testid="admin-new-game-upload-input"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const MAX = 5 * 1024 * 1024;
                        if (f.size > MAX) {
                          toast.error(`File too large. Max 5 MB.`);
                          e.target.value = "";
                          return;
                        }
                        try {
                          const text = await f.text();
                          const inferredSlug = newGameForm.slug || f.name.replace(/\.(html?|htm)$/i, "").toLowerCase().replace(/[^a-z0-9-]+/g, "-");
                          const inferredTitle = newGameForm.title || f.name.replace(/\.(html?|htm)$/i, "");
                          setNewGameForm({ ...newGameForm, html: text, slug: inferredSlug, title: inferredTitle });
                          toast.success(`Loaded ${f.name}`);
                        } catch {
                          toast.error("Could not read file.");
                        } finally {
                          e.target.value = "";
                        }
                      }}
                    />
                  </label>
                  <span className="font-mono text-[10px] text-white/40 self-center">
                    {newGameForm.html ? `${newGameForm.html.length.toLocaleString()} CHARS · ${(new Blob([newGameForm.html]).size / 1024).toFixed(1)} KB` : "no file loaded"}
                  </span>
                </div>
                <textarea
                  value={newGameForm.html}
                  onChange={(e) => setNewGameForm({ ...newGameForm, html: e.target.value })}
                  placeholder="<!DOCTYPE html><html>..."
                  spellCheck={false}
                  className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none font-mono text-xs p-3 leading-relaxed resize-y"
                  style={{ minHeight: 200, height: 260, tabSize: 2 }}
                  data-testid="admin-new-game-html"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowNewGame(false); setNewGameForm({ slug: "", title: "", html: "", position: 0 }); }}
                  className="font-mono text-[11px] tracking-widest px-4 py-2 border border-white/20 text-white/70 hover:text-white"
                  data-testid="admin-new-game-cancel"
                >
                  CANCEL
                </button>
                <button
                  onClick={async () => {
                    if (!newGameForm.slug) return toast.error("Slug required");
                    if (!newGameForm.title) return toast.error("Title required");
                    if (!newGameForm.html) return toast.error("HTML content required");
                    setCreatingGame(true);
                    try {
                      await api.post("/admin/games", {
                        slug: newGameForm.slug,
                        title: newGameForm.title,
                        html: newGameForm.html,
                        position: newGameForm.position || 0,
                        active: true,
                      });
                      toast.success(`Created "${newGameForm.title}"`);
                      const r = await api.get("/admin/games");
                      setGames(r.data || []);
                      const nd = {};
                      for (const gg of r.data || []) nd[gg.slug] = { title: gg.title, html: gg.html };
                      setGameDrafts(nd);
                      setShowNewGame(false);
                      setNewGameForm({ slug: "", title: "", html: "", position: 0 });
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Create failed");
                    } finally {
                      setCreatingGame(false);
                    }
                  }}
                  disabled={creatingGame}
                  className="font-mono text-[11px] tracking-widest px-4 py-2 bg-[#E63946] text-white hover:bg-[#C92A36] disabled:opacity-40"
                  data-testid="admin-new-game-create"
                >
                  {creatingGame ? "CREATING…" : "CREATE GAME"}
                </button>
              </div>
            </div>
          )}

          {games.length === 0 && (
            <div className="font-mono text-xs text-white/40">No games in the database.</div>
          )}

          <div className="space-y-8">
            {games.map((g) => {
              const draft = gameDrafts[g.slug] || { title: g.title, html: g.html };
              const dirty = draft.title !== g.title || draft.html !== g.html;
              const playUrl = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/games/${g.slug}/play`;
              return (
                <div
                  key={g.slug}
                  className="bg-[#151515] border border-white/10 p-5 space-y-3"
                  data-testid={`admin-game-${g.slug}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-mono text-[10px] tracking-widest text-[#FCA311]">
                        // SLUG: {g.slug} · UPDATED {g.updated_at?.slice(0, 16).replace("T", " ")}
                      </div>
                      <input
                        value={draft.title}
                        onChange={(e) => setGameDrafts({ ...gameDrafts, [g.slug]: { ...draft, title: e.target.value } })}
                        className="mt-1 bg-black border border-white/10 px-3 py-2 font-display text-2xl w-full sm:w-96 focus:border-[#E63946] outline-none"
                        placeholder="Game title"
                        data-testid={`game-title-${g.slug}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <a
                        href={playUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[11px] tracking-widest text-white/80 border border-white/20 px-3 py-2 hover:border-[#FCA311] hover:text-[#FCA311]"
                        data-testid={`game-preview-${g.slug}`}
                      >
                        PREVIEW ↗
                      </a>
                      <label
                        className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/70 hover:border-[#FCA311] hover:text-[#FCA311] cursor-pointer inline-flex items-center gap-1.5"
                        data-testid={`game-upload-label-${g.slug}`}
                      >
                        <Upload className="w-3 h-3" /> UPLOAD .HTML
                        <input
                          type="file"
                          accept=".html,.htm,text/html"
                          className="hidden"
                          data-testid={`game-upload-input-${g.slug}`}
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            const MAX = 5 * 1024 * 1024; // 5 MB safety cap
                            if (f.size > MAX) {
                              toast.error(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
                              e.target.value = "";
                              return;
                            }
                            try {
                              const text = await f.text();
                              const inferredTitle = draft.title || f.name.replace(/\.(html?|htm)$/i, "");
                              setGameDrafts({
                                ...gameDrafts,
                                [g.slug]: { title: inferredTitle, html: text },
                              });
                              toast.success(`Loaded ${f.name} — click SAVE to publish.`);
                            } catch {
                              toast.error("Could not read file. Make sure it's a plain-text .html file.");
                            } finally {
                              e.target.value = "";
                            }
                          }}
                        />
                      </label>
                      <button
                        onClick={() => setGameDrafts({ ...gameDrafts, [g.slug]: { title: g.title, html: g.html } })}
                        disabled={!dirty || savingGame === g.slug}
                        className="font-mono text-[11px] tracking-widest px-3 py-2 border border-white/20 text-white/60 hover:text-white disabled:opacity-30"
                        data-testid={`game-reset-${g.slug}`}
                      >
                        RESET
                      </button>
                      <button
                        onClick={async () => {
                          setSavingGame(g.slug);
                          try {
                            await api.put(`/admin/games/${g.slug}`, { html: draft.html, title: draft.title });
                            toast.success(`${draft.title} saved`);
                            const r = await api.get("/admin/games");
                            setGames(r.data || []);
                            const nd = {};
                            for (const gg of r.data || []) nd[gg.slug] = { title: gg.title, html: gg.html };
                            setGameDrafts(nd);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Save failed");
                          } finally {
                            setSavingGame(null);
                          }
                        }}
                        disabled={!dirty || savingGame === g.slug}
                        className="font-mono text-[11px] tracking-widest px-3 py-2 bg-[#E63946] text-white hover:bg-[#C92A36] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`game-save-${g.slug}`}
                      >
                        {savingGame === g.slug ? "SAVING…" : dirty ? "SAVE" : "SAVED"}
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete game "${g.title}" permanently? This cannot be undone.`)) return;
                          try {
                            await api.delete(`/admin/games/${g.slug}`);
                            toast.success(`Deleted ${g.title}`);
                            const r = await api.get("/admin/games");
                            setGames(r.data || []);
                            const nd = {};
                            for (const gg of r.data || []) nd[gg.slug] = { title: gg.title, html: gg.html };
                            setGameDrafts(nd);
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Delete failed");
                          }
                        }}
                        className="p-2 border border-white/20 text-white/60 hover:border-[#E63946] hover:text-[#E63946]"
                        title="Delete game"
                        data-testid={`game-delete-${g.slug}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={draft.html}
                    onChange={(e) => setGameDrafts({ ...gameDrafts, [g.slug]: { ...draft, html: e.target.value } })}
                    spellCheck={false}
                    className="w-full bg-black border border-white/10 focus:border-[#E63946] outline-none font-mono text-xs p-3 leading-relaxed resize-y"
                    style={{ minHeight: 320, height: 420, tabSize: 2 }}
                    data-testid={`game-html-${g.slug}`}
                  />

                  <div className="font-mono text-[10px] tracking-widest text-white/40">
                    {draft.html.length.toLocaleString()} CHARS · {(new Blob([draft.html]).size / 1024).toFixed(1)} KB
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <div className="space-y-3" data-testid="admin-contacts-list">
          {contacts.length === 0 && <div className="text-white/40 font-mono text-sm">No messages yet.</div>}
          {contacts.map((c) => (
            <div key={c.id} className="bg-[#151515] border border-white/10 p-4">
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="font-display text-lg">{c.name}</div>
                  <div className="font-mono text-xs text-white/50">{c.email} · {c.subject || "(no subject)"}</div>
                </div>
                <div className="font-mono text-[10px] text-white/40">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <div className="mt-2 text-sm text-white/80 whitespace-pre-wrap">{c.message}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div data-testid="admin-users-tab">
          <div className="bg-[#151515] border border-[#E63946] p-6 mb-6">
            <div className="font-display text-2xl mb-2">ADD ADMIN</div>
            <p className="font-mono text-xs text-white/50 mb-4">Create a new admin user with a username + password.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                placeholder="USERNAME"
                value={newAdminForm.username}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, username: e.target.value })}
                className="bg-[#0A0A0A] border border-white/20 px-3 py-2"
                data-testid="new-admin-username"
              />
              <input
                placeholder="PASSWORD (min 8)"
                type="password"
                value={newAdminForm.password}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                className="bg-[#0A0A0A] border border-white/20 px-3 py-2"
                data-testid="new-admin-password"
              />
              <input
                placeholder="DISPLAY NAME (optional)"
                value={newAdminForm.name}
                onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                className="bg-[#0A0A0A] border border-white/20 px-3 py-2"
              />
            </div>
            <button onClick={createAdmin} className="mt-4 font-display text-lg bg-[#E63946] text-white px-6 py-3" data-testid="create-admin-btn">
              CREATE ADMIN
            </button>
          </div>

          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">CURRENT ADMINS</div>
          <div className="space-y-2" data-testid="admin-users-list">
            {users.map((u) => (
              <div key={u.user_id} className="bg-[#151515] border border-white/10 p-4 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display text-lg">{u.username || u.name}</div>
                  <div className="font-mono text-xs text-white/50">
                    {u.email || "(username-only)"} · created {new Date(u.created_at).toLocaleDateString()}
                  </div>
                </div>
                {u.user_id !== user.user_id ? (
                  <button onClick={() => removeAdmin(u.user_id)} className="font-mono text-xs px-3 py-2 border border-white/20 hover:border-[#E63946]" data-testid={`remove-admin-${u.user_id}`}>
                    REVOKE ACCESS
                  </button>
                ) : (
                  <div className="font-mono text-[10px] text-[#FCA311]">YOU</div>
                )}
              </div>
            ))}
          </div>

          {/* ---------- CREATORS management ---------- */}
          <div className="bg-[#151515] border border-[#FCA311] p-6 mt-10 mb-6" data-testid="admin-creators-panel">
            <div className="font-display text-2xl mb-2">ADD CREATOR</div>
            <p className="font-mono text-xs text-white/50 mb-4">
              Creators can sign in at <code className="text-[#FCA311]">/signin</code> and upload audio, video and photos to the library. They CANNOT access this admin panel.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input placeholder="USERNAME" value={newCreatorForm.username} onChange={(e) => setNewCreatorForm({ ...newCreatorForm, username: e.target.value })} className="bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="new-creator-username" />
              <input placeholder="PASSWORD (min 8)" type="password" value={newCreatorForm.password} onChange={(e) => setNewCreatorForm({ ...newCreatorForm, password: e.target.value })} className="bg-[#0A0A0A] border border-white/20 px-3 py-2" data-testid="new-creator-password" />
              <input placeholder="DISPLAY NAME (optional)" value={newCreatorForm.name} onChange={(e) => setNewCreatorForm({ ...newCreatorForm, name: e.target.value })} className="bg-[#0A0A0A] border border-white/20 px-3 py-2" />
            </div>
            <button onClick={createCreator} className="mt-4 font-display text-lg bg-[#FCA311] text-black px-6 py-3" data-testid="create-creator-btn">
              CREATE CREATOR
            </button>
          </div>

          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">CURRENT CREATORS</div>
          <div className="space-y-2" data-testid="admin-creators-list">
            {creators.length === 0 && (
              <div className="font-mono text-xs text-white/30 border border-dashed border-white/10 p-6 text-center">
                No creators yet — add one above so they can start submitting content.
              </div>
            )}
            {creators.map((c) => (
              <div key={c.user_id} className="bg-[#151515] border border-white/10">
                <div className="p-4 flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-display text-lg">{c.username || c.name}</div>
                    <div className="font-mono text-xs text-white/50">
                      {c.track_count || 0} track{c.track_count === 1 ? "" : "s"} · {c.photo_count || 0} photo{c.photo_count === 1 ? "" : "s"} · created {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => toggleCreatorUploads(c.user_id)} className="font-mono text-xs px-3 py-2 border border-white/20 hover:border-[#FCA311] hover:text-[#FCA311]" data-testid={`creator-view-uploads-${c.user_id}`}>
                      {expandedCreator === c.user_id ? "HIDE" : "VIEW"} UPLOADS
                    </button>
                    <button onClick={() => setCreatorPwPrompt({ user_id: c.user_id, value: "" })} className="font-mono text-xs px-3 py-2 border border-white/20 hover:border-[#67E8F9] hover:text-[#67E8F9]" data-testid={`creator-reset-pw-${c.user_id}`}>
                      RESET PW
                    </button>
                    <button onClick={() => deleteCreator(c.user_id, c.username)} className="font-mono text-xs px-3 py-2 border border-white/20 hover:border-[#E63946] hover:text-[#E63946]" data-testid={`creator-delete-${c.user_id}`}>
                      DELETE
                    </button>
                  </div>
                </div>
                {/* Password reset inline prompt */}
                {creatorPwPrompt?.user_id === c.user_id && (
                  <div className="border-t border-white/10 bg-[#0A0A0A] p-4 flex flex-wrap items-center gap-2" data-testid={`creator-pw-prompt-${c.user_id}`}>
                    <span className="font-mono text-[10px] tracking-widest text-[#67E8F9]">NEW PASSWORD</span>
                    <input type="password" value={creatorPwPrompt.value} onChange={(e) => setCreatorPwPrompt({ ...creatorPwPrompt, value: e.target.value })} placeholder="min 8 characters" autoFocus className="flex-1 min-w-[180px] bg-[#151515] border border-white/20 focus:border-[#67E8F9] outline-none px-3 py-2 font-mono text-sm" data-testid={`creator-pw-input-${c.user_id}`} />
                    <button onClick={() => resetCreatorPassword(c.user_id, creatorPwPrompt.value)} className="font-mono text-xs px-3 py-2 bg-[#67E8F9] text-black">SET PASSWORD</button>
                    <button onClick={() => setCreatorPwPrompt(null)} className="font-mono text-xs px-3 py-2 border border-white/20">CANCEL</button>
                  </div>
                )}
                {/* Uploads expansion */}
                {expandedCreator === c.user_id && creatorUploads[c.user_id] && (
                  <div className="border-t border-white/10 bg-[#0A0A0A] p-4 space-y-2" data-testid={`creator-uploads-${c.user_id}`}>
                    {(creatorUploads[c.user_id].tracks.length === 0 && creatorUploads[c.user_id].photos.length === 0) && (
                      <div className="font-mono text-xs text-white/30">Nothing uploaded yet.</div>
                    )}
                    {creatorUploads[c.user_id].tracks.map((t) => (
                      <div key={t.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center border border-white/5 px-3 py-2">
                        <span className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">{(t.media_type || "audio").toUpperCase()}</span>
                        <div className="min-w-0">
                          <div className="font-display text-sm truncate">{t.title || "(untitled)"}</div>
                          <div className="font-mono text-[10px] text-white/40 truncate">{t.artist || "—"} · {t.youtube_id ? `YT:${t.youtube_id}` : (t.audio_url ? "file" : "no source")}</div>
                        </div>
                        <span className="font-mono text-[10px] text-white/30">{new Date(t.created_at || Date.now()).toLocaleDateString()}</span>
                      </div>
                    ))}
                    {creatorUploads[c.user_id].photos.map((p) => (
                      <div key={p.id} className="grid grid-cols-[auto_1fr_auto] gap-3 items-center border border-white/5 px-3 py-2">
                        <span className="font-mono text-[10px] tracking-[0.3em] text-[#67E8F9]">PHOTO</span>
                        <div className="min-w-0 font-mono text-xs text-white/70 truncate">{p.caption || p.image_path || "(no caption)"}</div>
                        <span className="font-mono text-[10px] text-white/30">{new Date(p.created_at || Date.now()).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => (
  <div className="font-mono text-[10px] tracking-widest text-white/50 mb-1">{children}</div>
);

function SectionNameEditor({ section, onSave }) {
  const [name, setName] = useState(section.name || "");
  const dirty = name !== section.name;
  return (
    <div className="border border-white/10 p-3" data-testid={`section-editor-${section.position}`}>
      <div className="font-mono text-[10px] tracking-widest text-[#FCA311] mb-2">SECTION {section.position}</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-white/20 px-2 py-1 font-display text-base"
        data-testid={`section-name-input-${section.position}`}
      />
      <button
        onClick={() => onSave(section.position, name)}
        disabled={!dirty || !name.trim()}
        className="mt-2 font-mono text-[10px] px-3 py-1 border border-white/20 hover:border-[#E63946] disabled:opacity-30"
        data-testid={`section-save-btn-${section.position}`}
      >
        SAVE
      </button>
    </div>
  );
}


function TrafficPanel({ range, setRange, data, setData, loading, setLoading }) {
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/summary?days=${range}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  }, [range, setData, setLoading]);

  const daily = data?.daily || [];
  const maxViews = Math.max(1, ...daily.map((d) => d.views));

  return (
    <div className="space-y-6" data-testid="admin-traffic">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl">TRAFFIC</h2>
          <p className="text-white/50 text-sm mt-1 max-w-2xl">
            Anonymous page-view tracker — no IPs stored, admin routes and bots excluded. Data resets whenever you clear the collection.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90, 365].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              className={`font-mono text-[10px] tracking-widest px-3 py-2 border transition-colors ${range === d ? "bg-white text-black border-white" : "border-white/20 text-white/60 hover:border-white"}`}
              data-testid={`traffic-range-${d}`}
            >
              {d === 365 ? "1Y" : `${d}D`}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="font-mono text-sm text-white/40 py-10">Loading analytics…</div>}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="glass-card rounded-2xl p-5" data-testid="traffic-kpi-views">
              <div className="font-mono text-[10px] tracking-widest text-white/40">TOTAL VIEWS</div>
              <div className="font-display text-4xl mt-2">{data.total_views.toLocaleString()}</div>
            </div>
            <div className="glass-card rounded-2xl p-5" data-testid="traffic-kpi-visitors">
              <div className="font-mono text-[10px] tracking-widest text-white/40">UNIQUE VISITORS</div>
              <div className="font-display text-4xl mt-2 text-[#FCA311]">{data.unique_visitors.toLocaleString()}</div>
            </div>
            <div className="glass-card rounded-2xl p-5" data-testid="traffic-kpi-avg">
              <div className="font-mono text-[10px] tracking-widest text-white/40">AVG VIEWS / DAY</div>
              <div className="font-display text-4xl mt-2 text-[#E63946]">
                {Math.round(data.total_views / Math.max(1, data.range_days))}
              </div>
            </div>
          </div>

          {/* Bar chart */}
          <div className="glass-card rounded-2xl p-6" data-testid="traffic-chart">
            <div className="font-mono text-[10px] tracking-widest text-white/40 mb-4">// VIEWS PER DAY</div>
            {daily.length === 0 ? (
              <div className="text-white/40 font-mono text-sm">No data yet.</div>
            ) : (
              <div className="flex items-end gap-1 h-48">
                {daily.map((d) => (
                  <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.day} · ${d.views} views · ${d.visitors} visitors`}>
                    <div
                      className="w-full rounded-t bg-gradient-to-t from-[#E63946] to-[#FCA311] group-hover:opacity-100 opacity-80 transition-opacity"
                      style={{ height: `${(d.views / maxViews) * 100}%` }}
                    />
                  </div>
                ))}
              </div>
            )}
            {daily.length > 0 && (
              <div className="flex justify-between font-mono text-[9px] text-white/40 mt-2">
                <span>{daily[0].day}</span>
                <span>{daily[daily.length - 1].day}</span>
              </div>
            )}
          </div>

          {/* Top paths + referrers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-card rounded-2xl p-6" data-testid="traffic-top-paths">
              <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">// TOP PAGES</div>
              <div className="space-y-2">
                {(data.top_paths || []).map((p) => (
                  <div key={p.path} className="flex items-center justify-between gap-3 py-1 border-b border-white/5">
                    <span className="font-mono text-xs text-white truncate">{p.path}</span>
                    <span className="font-mono text-xs text-[#FCA311]">{p.views}</span>
                  </div>
                ))}
                {(data.top_paths || []).length === 0 && <div className="text-white/40 font-mono text-sm">No data yet.</div>}
              </div>
            </div>
            <div className="glass-card rounded-2xl p-6" data-testid="traffic-top-refs">
              <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">// TOP REFERRERS</div>
              <div className="space-y-2">
                {(data.top_referrers || []).map((r) => (
                  <div key={r.referrer} className="flex items-center justify-between gap-3 py-1 border-b border-white/5">
                    <span className="font-mono text-xs text-white/80 truncate max-w-[70%]" title={r.referrer}>{r.referrer}</span>
                    <span className="font-mono text-xs text-[#FCA311]">{r.views}</span>
                  </div>
                ))}
                {(data.top_referrers || []).length === 0 && <div className="text-white/40 font-mono text-sm">No external referrers yet.</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
